(() => {
  const T = (window.Theme = window.Theme || {});

  /* Smooth closing for every <dialog> (drawers, filter sheet, lightbox, personaliser, search, menu):
   * add .is-closing, let its CSS exit animation run, then really close. Escape goes through here too. */
  T.closeDialog = (dialog) => {
    if (!dialog || !dialog.open) return;
    if (dialog.classList.contains('is-closing')) return;
    if (typeof dialog.beforeClose === 'function' && dialog.beforeClose() === true) return; // dialog runs its own exit
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) { dialog.close(); return; }
    dialog.classList.add('is-closing');
    let done = false;
    const onEnd = (e) => { if (e.target === dialog && !e.pseudoElement) finish(); };
    const finish = () => {
      if (done) return;
      done = true;
      dialog.removeEventListener('animationend', onEnd);
      /* re-opened while it was animating out (open() drops is-closing): leave it open */
      if (!dialog.classList.contains('is-closing')) return;
      dialog.classList.remove('is-closing');
      dialog.close();
    };
    dialog.addEventListener('animationend', onEnd);
    setTimeout(finish, 420);
  };
  document.addEventListener('cancel', (e) => {
    if (e.target instanceof HTMLDialogElement) { e.preventDefault(); T.closeDialog(e.target); }
  }, true);
  /* A closed dialog hands focus back to its opener, and Safari then draws the keyboard ring on it even after a tap
   * (the field inside the dialog had a visible focus). After a tap or click, the opener gets no ring until it blurs. */
  let pointerLast = false;
  /* html.kb-nav while the keyboard is in use: Klaviyo moves focus to its teaser when the popup closes, and that ring
   * only makes sense for keyboard users (base.css) */
  document.addEventListener('pointerdown', () => { pointerLast = true; document.documentElement.classList.remove('kb-nav'); }, true);
  document.addEventListener('keydown', (e) => { if (e.key === 'Tab' || e.key === 'Enter' || e.key === ' ') { pointerLast = false; document.documentElement.classList.add('kb-nav'); } }, true);
  document.addEventListener('close', (e) => {
    if (!(e.target instanceof HTMLDialogElement) || !pointerLast) return;
    setTimeout(() => {
      const el = document.activeElement;
      if (!el || el === document.body || el.closest('dialog')) return;
      el.classList.add('no-ring');
      el.addEventListener('blur', () => el.classList.remove('no-ring'), { once: true });
    });
  }, true);

  /* In-page links (e.g. the star rating → #reviews) glide to their target instead of jumping. The
   * header offset comes from scroll-padding-top on <html>. */
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const a = e.target.closest('a[href*="#"]');
    if (!a || a.classList.contains('skip-link') || a.origin !== location.origin || a.pathname !== location.pathname || a.hash.length < 2) return;
    let target;
    try { target = document.getElementById(decodeURIComponent(a.hash.slice(1))); } catch { return; }
    if (!target || target.closest('dialog, [role="tabpanel"]')) return;
    e.preventDefault();
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    history.pushState(history.state, '', a.hash); // keep other scripts' state (facets, terms)
  });

  T.formatMoney = (cents, format = T.moneyFormat || '{{amount}}') => {
    format = String(format).replace(/<[^>]*>/g, '');
    const value = Number(cents) / 100;
    const fmt = (n, d, ts = ',', ds = '.') => {
      const [i, f] = n.toFixed(d).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, ts) + (f ? ds + f : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        default: return fmt(value, 2);
      }
    });
  };

  T.freeShippingCents = () => {
    const fs = T.freeShipping;
    if (!fs) return null;
    if (!fs.convert) return fs.amount;
    const rate = Number(window.Shopify?.currency?.rate || 1);
    return Math.ceil((fs.amount * rate) / 100) * 100;
  };

  T.debounce = (fn, ms = 250) => {
    let t;
    return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
  };

  /* Tall sticky columns (product page photos + buy panel, a set's page; desktop): a column taller than the window
   * scrolls with the page both ways — down until its bottom shows, back up until its top shows — and stays put in
   * between, so nothing in it is out of reach while the other column scrolls. It only moves the column's sticky `top`
   * (CSS custom property `prop`, read by its `top: var(...)`), so nothing changes in its look.
   * cols: [[element, '--prop', gap below the header in px], …] */
  T.stickyCols = (cols) => {
    cols = cols.filter(([el]) => el);
    if (!cols.length || !('ResizeObserver' in window)) return;
    const wide = matchMedia('(min-width: 990px)');
    const state = cols.map(([el, prop, gap = 0]) => ({ el, prop, gap, t: null, min: 0, max: 0 }));
    let lastY = scrollY, raf = 0;
    const put = (s) => s.el.style.setProperty(s.prop, `${Math.round(s.t)}px`);
    const limits = () => {
      const hh = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 64;
      // room for the sticky add-to-bag bar (a hidden one still counts its usual height)
      const bar = document.querySelector('.pdp-sticky');
      const bottom = (bar ? bar.offsetHeight || 84 : 0) + 24;
      for (const s of state) {
        s.max = hh + s.gap;
        s.min = Math.min(s.max, innerHeight - s.el.offsetHeight - bottom);
        // first time: as before (a tall column starts scrolled to show its bottom only once the page has moved)
        s.t = s.t == null ? (scrollY > 0 ? s.min : s.max) : Math.max(s.min, Math.min(s.max, s.t));
        put(s);
      }
    };
    const onScroll = () => {
      raf = 0;
      const d = scrollY - lastY;
      lastY = scrollY;
      if (!d || !wide.matches) return;
      for (const s of state) {
        if (s.min >= s.max) continue;
        const t = Math.max(s.min, Math.min(s.max, s.t - d));
        if (t !== s.t) { s.t = t; put(s); }
      }
    };
    addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(onScroll); }, { passive: true });
    const ro = new ResizeObserver(() => limits());
    state.forEach((s) => ro.observe(s.el));
    addEventListener('resize', T.debounce(limits, 120));
    limits();
  };

  T.fetchSections = async (url, ids) => {
    const u = new URL(url, location.origin);
    u.searchParams.set('sections', ids.join(','));
    const r = await fetch(u);
    if (!r.ok) throw new Error(`Sections request failed (${r.status})`);
    return r.json();
  };

  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------------------------------------------------------------- Cart */
  const CART_SECTIONS = ['cart-drawer', 'cart-count'];

  /* Remembers which cart control has focus so it can be restored after a re-render. */
  const FOCUS_ATTRS = ['data-qty-step', 'data-qty', 'data-remove', 'data-discount-input', 'data-discount-apply', 'data-discount-remove', 'data-discount-toggle', 'data-apply-code', 'data-upsell-add', 'data-gift-packaging', 'data-cart-note', 'data-cart-taxid', 'data-ask'];
  T.captureFocus = (root) => {
    const el = document.activeElement;
    const open = [...root.querySelectorAll('details[id][open]')].map((d) => d.id);
    const panels = [...root.querySelectorAll('[data-ask-panel][id]:not([hidden])')].map((d) => d.id);
    const reopen = () => {
      open.forEach((id) => { const d = root.querySelector(`details#${CSS.escape(id)}`); if (d) d.open = true; });
      panels.forEach((id) => T.cartUI.ask(root, id, true));
    };
    if (!el || el === document.body || !root.contains(el)) return reopen;
    const line = el.closest('[data-line-key]')?.dataset.lineKey;
    const attr = FOCUS_ATTRS.find((a) => el.hasAttribute(a));
    const val = attr && el.getAttribute(attr);
    const sel = attr ? `[${attr}${val ? `="${CSS.escape(val)}"` : ''}]` : el.id ? `#${CSS.escape(el.id)}` : null;
    return () => {
      reopen();
      const scope = line ? root.querySelector(`[data-line-key="${CSS.escape(line)}"]`) : root;
      const target = (scope && sel && scope.querySelector(sel)) || root.querySelector('[data-focus-fallback]');
      target?.focus({ preventScroll: true });
    };
  };

  /* Scripts inside re-rendered HTML do not run; re-create the ones in the express checkout wrapper. */
  T.revive = (root) => {
    const express = root.querySelectorAll('.cart-express');
    if (!express.length) return;
    express.forEach((wrap) => wrap.querySelectorAll('script').forEach((s) => {
      const n = document.createElement('script');
      [...s.attributes].forEach((a) => n.setAttribute(a.name, a.value));
      n.textContent = s.textContent;
      s.replaceWith(n);
    }));
    try { window.Shopify?.PaymentButton?.init?.(); } catch (e) {}
  };

  T.cart = {
    async request(url, body) {
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: CART_SECTIONS, sections_url: location.pathname })
      });
      const data = await r.json();
      if (!r.ok || data.status) {
        // Insights (assets/insights.js) records failed bag requests
        document.dispatchEvent(new CustomEvent('cart:error', { detail: { endpoint: url, status: r.status, message: String(data.description || data.message || '') } }));
        throw Object.assign(new Error(data.description || data.message || T.strings.error), { data, httpStatus: r.status });
      }
      return data;
    },
    async add(items, { open = true } = {}) {
      const list = Array.isArray(items) ? items : [items];
      const data = await this.request(T.routes.cartAdd + '.js', { items: list });
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { action: 'add', items: list } }));
      if (open) T.cart.open();
      return data;
    },
    async change(key, quantity) {
      const data = await this.request(T.routes.cartChange + '.js', { id: key, quantity });
      this.render(data.sections, data);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { action: 'change' } }));
      return data;
    },
    /* `silent` saves without re-rendering or events (used while typing the gift message). */
    async update(payload, { silent = false } = {}) {
      const data = await this.request(T.routes.cartUpdate + '.js', payload);
      if (silent) return data;
      this.render(data.sections, data);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { action: 'update' } }));
      return data;
    },
    async refresh() {
      const sections = await T.fetchSections(location.pathname, CART_SECTIONS);
      this.render(sections);
    },
    render(sections, cart) {
      if (!sections) return;
      for (const id of CART_SECTIONS) {
        const html = sections[id];
        if (!html) continue;
        const fresh = parse(html).getElementById(`shopify-section-${id}`);
        document.querySelectorAll(`[data-section-target="${id}"]`).forEach((el) => {
          const inner = fresh?.querySelector(`[data-section-target="${id}"]`);
          if (!inner) return;
          const restore = T.captureFocus(el);
          el.replaceChildren(...inner.cloneNode(true).childNodes);
          T.revive(el);
          T.cartUI.restoreNote(el);
          restore();
        });
      }
      T.cartUI.syncCodes(cart);
      document.dispatchEvent(new CustomEvent('cart:rendered'));
    },
    open() { document.querySelector('cart-drawer')?.open(); }
  };

  /* Shared by the drawer and the /cart page (assets/cart-page.js). `wait` resolves once the caller's markup is re-rendered. */
  const EU = 'AT,BE,BG,HR,CY,CZ,DK,EE,FI,FR,DE,GR,HU,IE,IT,LV,LT,LU,MT,NL,PL,PT,RO,SK,SI,ES,SE'.split(',');
  /* Days nothing ships or moves: Spanish national holidays (the workshop dispatches from Spain) for everyone, plus
   * England & Wales bank holidays for UK deliveries. Used by the cart / order estimate and the product page. Extend
   * the lists each year. */
  const HOLIDAYS = {
    ES: ['2026-01-01', '2026-01-06', '2026-04-03', '2026-05-01', '2026-08-15', '2026-10-12', '2026-12-08', '2026-12-25',
      '2027-01-01', '2027-01-06', '2027-03-26', '2027-10-12', '2027-11-01', '2027-12-06', '2027-12-08'],
    GB: ['2026-01-01', '2026-04-03', '2026-04-06', '2026-05-04', '2026-05-25', '2026-08-31', '2026-12-25', '2026-12-28',
      '2027-01-01', '2027-03-26', '2027-03-29', '2027-05-03', '2027-05-31', '2027-08-30', '2027-12-27', '2027-12-28']
  };
  T.offDay = (d, cc) => {
    if (d.getUTCDay() === 0 || d.getUTCDay() === 6) return true;
    const iso = d.toISOString().slice(0, 10);
    return HOLIDAYS.ES.includes(iso) || (String(cc || '').toUpperCase() === 'GB' && HOLIDAYS.GB.includes(iso));
  };
  T.cartUI = {
    async setQty(root, line, qty, wait = () => {}) {
      if (!line) return;
      const key = line.dataset.lineKey;
      line.setAttribute('aria-busy', 'true');
      try {
        const group = line.dataset.customisationId;
        const set = line.dataset.bundleGroup;
        if (group || set) {
          /* Personalised lines move together (fee and base share one quantity), and so do the pieces of a set: one
             piece alone would lose the set discount. */
          const updates = {};
          const sel = group ? `[data-customisation-id="${CSS.escape(group)}"]` : `[data-bundle-group="${CSS.escape(set)}"]`;
          root.querySelectorAll(sel).forEach((l) => (updates[l.dataset.lineKey] = qty));
          await T.cart.update({ updates });
        } else {
          await T.cart.change(key, qty);
        }
        await wait();
      } catch (err) {
        /* 400 "no valid id or line parameter": the line is already gone or changed (a double click, another tab, a
         * drawer drawn before the bag changed). Redraw from the real bag instead of showing Shopify's English error. */
        if (err?.httpStatus === 400 || /no valid id or line/i.test(`${err?.message} ${err?.data?.message}`)) {
          try { await T.cart.refresh(); await wait(); document.dispatchEvent(new CustomEvent('cart:updated', { detail: { action: 'refresh' } })); return; } catch (e) { /* fall through */ }
        }
        const l = root.querySelector(`[data-line-key="${CSS.escape(key)}"]`) || line;
        const input = l.querySelector('input[data-qty]');
        if (input) input.value = input.defaultValue;
        l.querySelector('[data-line-error]')?.replaceChildren(document.createTextNode(err.message || T.strings.error));
        l.removeAttribute('aria-busy');
      }
    },
    codes(root) {
      return (root.querySelector('[data-cart-discount]')?.dataset.codes || '').split(',').map((c) => c.trim()).filter(Boolean);
    },
    /* Applied-code chips come from the cart JSON: Shopify's Section Rendering leaves cart.discount_codes empty,
     * so the server-rendered chips (and data-codes) can't be trusted after an AJAX re-render. Without a cart
     * object (add/change responses), /cart.js is fetched once. */
    async syncCodes(cart) {
      const boxes = document.querySelectorAll('[data-cart-discount]');
      if (!boxes.length) return;
      if (!cart || !Array.isArray(cart.discount_codes)) {
        try { cart = await (await fetch(T.routes.cart + '.js', { headers: { Accept: 'application/json' } })).json(); } catch (e) { return; }
      }
      const list = (cart.discount_codes || []).filter((d) => d && d.code);
      const esc = (v) => String(v).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
      boxes.forEach((box) => {
        box.dataset.codes = list.map((d) => d.code).join(',');
        box.querySelectorAll('.cart-discount__chips, .cart-discount__msg').forEach((n) => n.remove());
        if (!list.length) return;
        const removeLabel = box.dataset.removeLabel || '';
        const notApplicable = box.dataset.notApplicable || '';
        const icon = box.querySelector('template[data-discount-icon]')?.innerHTML || '×';
        const chips = list.map((d) => `<li class="cart-discount__chip${d.applicable === false ? ' is-invalid' : ''}"><span>${esc(d.code)}</span><button type="button" class="cart-discount__remove" data-discount-remove="${esc(d.code)}" aria-label="${esc(removeLabel.replace('[code]', d.code))}">${icon}</button></li>`).join('');
        const msgs = list.filter((d) => d.applicable === false).map((d) => `<p class="cart-discount__msg">${esc(notApplicable.replace('[code]', d.code))}</p>`).join('');
        box.insertAdjacentHTML('afterbegin', `<ul class="cart-discount__chips" role="list" aria-label="${esc(box.dataset.appliedLabel || '')}">${chips}</ul>${msgs}`);
        // The code panel is folded when the server drew a bag without codes: an applied code must stay in sight
        const panel = box.closest('[data-ask-panel]');
        if (panel?.id) T.cartUI.ask(panel.closest('cart-drawer') || document, panel.id, true);
      });
      document.querySelectorAll('[data-promo-cart]').forEach((n) => { n.dataset.applied = list.map((d) => d.code.toLowerCase()).join(','); });
    },
    async discount(root, codes, { wanted = '', wait = () => {} } = {}) {
      const box = root.querySelector('[data-cart-discount]');
      const msgs = { invalid: box?.dataset.invalid || T.strings.error, set: box?.dataset.setCombine };
      const show = (msg) => {
        const b = root.querySelector('[data-cart-discount]');
        const e = b?.querySelector('[data-discount-error]');
        if (e) e.textContent = msg;
        const d = b?.querySelector('details');
        if (d && msg) d.open = true;
      };
      box?.setAttribute('aria-busy', 'true');
      try {
        // A new code is saved without drawing Shopify's reply: drawn, an unknown code showed for ~3 s as a crossed-out
        // chip with "can't be used with the items in your bag" before being taken off again as "isn't valid".
        let data = await T.cart.update({ discount: [...new Set(codes)].join(',') }, { silent: Boolean(wanted) });
        if (!wanted) { await wait(); return; }
        // Shopify answers unknown and non-qualifying codes alike (applicable: false): take the code back off the
        // cart so it doesn't linger as a dead chip, and say why. Harber Club codes (CLUB-…) are real but need a
        // minimum order and the member's e-mail (known at checkout): they stay, and the chip says they don't apply yet
        // (syncCodes, run by the re-render, shows the "can't be used with the items in your bag" line under it).
        const hit = (data.discount_codes || []).find((d) => String(d.code).toLowerCase() === wanted.toLowerCase());
        if (hit && (hit.applicable !== false || /^(CLUB|FRIEND)-/i.test(wanted))) {
          T.cart.render(data.sections, data);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { action: 'update' } }));
          await wait();
        } else {
          data = await T.cart.update({ discount: [...new Set(codes)].filter((c) => c.toLowerCase() !== wanted.toLowerCase()).join(',') });
          await wait();
          // A set's price combines with no code by default (harberapp "Harber sets"): Shopify keeps whichever saves more
          show((data.items || []).some((i) => i.properties?._bundle) && msgs.set ? msgs.set : msgs.invalid);
        }
      } catch (err) {
        box?.removeAttribute('aria-busy');
        show(err.message || T.strings.error);
      }
    },
    /* One code change at a time: a second Apply (or Remove) waits for the first, and reads the applied codes only
     * when its turn comes (otherwise a valid code typed while a wrong one was being taken off was lost) */
    queue(fn) {
      const run = (this.chain || Promise.resolve()).then(fn, fn);
      this.chain = run.catch(() => {});
      return run;
    },
    applyFromInput(root, wait) {
      const box = root.querySelector('[data-cart-discount]');
      const input = box?.querySelector('[data-discount-input]');
      const code = (input?.value || '').trim();
      const err = box?.querySelector('[data-discount-error]');
      if (!code) { if (err) err.textContent = box.dataset.empty || ''; input?.focus(); return; }
      if (err) err.textContent = '';
      return this.queue(() => {
        const codes = this.codes(root).filter((c) => c.toLowerCase() !== code.toLowerCase());
        return this.discount(root, [...codes, code], { wanted: code, wait });
      });
    },
    applyCode(root, code, wait) {
      return this.queue(() => {
        const codes = this.codes(root).filter((c) => c.toLowerCase() !== code.toLowerCase());
        return this.discount(root, [...codes, code], { wanted: code, wait });
      });
    },
    removeCode(root, code, wait) {
      return this.queue(() => this.discount(root, this.codes(root).filter((c) => c.toLowerCase() !== code.toLowerCase()), { wait }));
    },
    /* "Estimated delivery: Tue 7 Oct – Thu 9 Oct" from the delivery_transit_days and ship_today_cutoff settings. */
    eta(root) {
      root.querySelectorAll('[data-eta]').forEach((el) => {
        const rules = {};
        (el.dataset.rules || '').split(/\r?\n/).forEach((l) => {
          const i = l.indexOf(':');
          if (i > 0) rules[l.slice(0, i).trim().toUpperCase()] = l.slice(i + 1).trim();
        });
        const cc = (el.dataset.country || T.country || '').toUpperCase();
        const rule = rules[cc] || (EU.includes(cc) && rules.EU) || rules['*'];
        const m = rule && /(\d+)\s*(?:-\s*(\d+))?/.exec(rule);
        if (!m) { el.hidden = true; return; }
        /* Personalised pieces take 1–2 more working days in the workshop */
        const pz = root.querySelector('.cart-line__pz') ? 1 : 0;
        const min = Number(m[1]) + pz;
        const max = Math.max(min, Number(m[2] || m[1]) + pz * 2);
        const tz = /^([+-])(\d{2})(\d{2})$/.exec(el.dataset.tz || '') || ['', '+', '00', '00'];
        const offset = (tz[1] === '-' ? -1 : 1) * (Number(tz[2]) * 60 + Number(tz[3]));
        const nowShop = new Date(Date.now() + offset * 60000);
        /* data-from (shop-local "YYYY-MM-DDTHH:MM:SS", e.g. when an order was placed) replaces "now". */
        const from = el.dataset.from && new Date(`${el.dataset.from}Z`);
        const shopNow = from && !Number.isNaN(from.getTime()) ? from : nowShop;
        const day = new Date(Date.UTC(shopNow.getUTCFullYear(), shopNow.getUTCMonth(), shopNow.getUTCDate()));
        const weekend = (d) => T.offDay(d, cc);
        const cut = /^(\d{1,2}):(\d{2})$/.exec((el.dataset.cutoff || '').trim());
        const late = cut && shopNow.getUTCHours() * 60 + shopNow.getUTCMinutes() >= Number(cut[1]) * 60 + Number(cut[2]);
        if (weekend(day) || late) do { day.setUTCDate(day.getUTCDate() + 1); } while (weekend(day));
        const plus = (n) => { const d = new Date(day); let i = 0; while (i < n) { d.setUTCDate(d.getUTCDate() + 1); if (!weekend(d)) i++; } return d; };
        let fmt;
        /* Same as the product page: plain "en" reads as en-GB and commas are dropped ("Tue 7 Oct"). */
        const loc = /^en$/i.test(T.locale || '') ? 'en-GB' : T.locale || undefined;
        try { fmt = new Intl.DateTimeFormat(loc, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }); } catch (e) { fmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }); }
        if (from && plus(max) < Date.UTC(nowShop.getUTCFullYear(), nowShop.getUTCMonth(), nowShop.getUTCDate())) { el.hidden = true; return; } // already past
        const a = fmt.format(plus(min)).replace(/,/g, '');
        const b = fmt.format(plus(max)).replace(/,/g, '');
        (el.querySelector('[data-eta-text]') || el).textContent = (el.dataset.label || '[dates]').replace('[dates]', a === b ? a : `${a} – ${b}`);
        el.hidden = false;
      });
    },
    freeShipping(root) {
      const bar = root.querySelector('[data-free-shipping]');
      if (!bar) return;
      const total = Number(bar.dataset.total || 0);
      const goal = T.freeShippingCents();
      if (!goal) { bar.hidden = true; return; }
      const left = Math.max(goal - total, 0);
      bar.style.setProperty('--progress', `${Math.min(100, (total / goal) * 100)}%`);
      const label = bar.querySelector('[data-free-shipping-label]');
      const html = left > 0 ? T.strings.freeShippingRemaining.replace('[amount]', `<strong>${T.formatMoney(left)}</strong>`) : T.strings.freeShippingReached;
      if (label && label.innerHTML !== html) label.innerHTML = html;
      bar.classList.toggle('is-complete', left === 0);
      bar.hidden = total === 0;
      return left;
    },
    paint(root) {
      const left = this.freeShipping(root);
      this.eta(root);
      T.fillFreeShipping?.(root);
      return left;
    }
  };

  /* "Have a code? · Need an invoice?" (cart drawer): shows or hides the panel a link controls. */
  T.cartUI.ask = (root, id, show) => {
    const panel = root.querySelector(`#${CSS.escape(id)}`);
    if (!panel) return;
    panel.hidden = !show;
    root.querySelectorAll(`[data-ask="${CSS.escape(id)}"]`).forEach((b) => b.setAttribute('aria-expanded', String(show)));
  };
  /* An opened panel left empty folds again when the shopper moves on (focus leaves it): no code typed and none on the
   * bag, or no tax ID. Not while a press inside the panel or on its own link is under way (Safari doesn't focus
   * buttons, so a click on Apply blurs the field with nowhere to go), nor while a code is being checked. */
  let askPress = { at: 0, el: null };
  document.addEventListener('pointerdown', (e) => { askPress = { at: performance.now(), el: e.target.closest?.('[data-ask-panel], [data-ask]') || null }; }, true);
  document.addEventListener('focusout', (e) => {
    const panel = e.target.closest?.('[data-ask-panel]');
    if (!panel?.id) return;
    setTimeout(() => {
      if (panel.hidden || !panel.isConnected || panel.contains(document.activeElement)) return;
      const own = askPress.el && (askPress.el === panel || askPress.el.dataset.ask === panel.id);
      if (own && performance.now() - askPress.at < 400) return;
      T.cartUI.foldIfEmpty(panel);
    }, 150);
  });
  /** A soft entrance (fade + small slide, base.css .is-entering) for a panel the shopper opened; re-renders that
   * reopen panels don't call it, so the bag doesn't animate on every update. */
  T.cartUI.enter = (el) => {
    if (!el) return;
    el.classList.remove('is-entering');
    void el.offsetWidth;
    el.classList.add('is-entering');
    el.addEventListener('animationend', () => el.classList.remove('is-entering'), { once: true });
  };
  // On the click, not the details' toggle event: toggle also fires for a gift row drawn open by a bag re-render
  document.addEventListener('click', (e) => {
    const d = e.target.closest?.('.cart-gift > summary')?.parentElement;
    if (d && !d.open) requestAnimationFrame(() => T.cartUI.enter(d.querySelector('.cart-gift__body')));
  });
  /** Folds a panel that holds nothing (no code typed or on the bag, nothing being checked, no tax ID). */
  T.cartUI.foldIfEmpty = (panel) => {
    if (panel.hidden || panel.querySelector('[aria-busy="true"], .cart-discount__chip')) return;
    if ([...panel.querySelectorAll('input, textarea')].some((f) => f.value.trim())) return;
    const err = panel.querySelector('[data-discount-error]');
    if (err) err.textContent = '';
    T.cartUI.ask(panel.closest('cart-drawer') || document, panel.id, false);
  };

  /* Discount, promo "Apply" and qty clicks: same wiring for <cart-drawer> and <cart-page>. */
  T.cartUI.onClick = (root, e, wait) => {
    const btn = e.target.closest('button');
    if (!btn || btn.type === 'submit') return false;
    if (btn.matches('[data-discount-apply]')) { T.cartUI.applyFromInput(root, wait); return true; }
    if (btn.matches('[data-discount-remove]')) { T.cartUI.removeCode(root, btn.dataset.discountRemove, wait); return true; }
    if (btn.matches('[data-apply-code]')) { T.cartUI.applyCode(root, btn.dataset.applyCode, wait); return true; }
    // "Complete the set" (snippets/cart-upsells): adds in place, in the drawer and on the /cart page
    if (btn.matches('[data-upsell-add]')) {
      const err = btn.closest('.upsell')?.querySelector('[data-upsell-error]');
      if (err) err.textContent = '';
      btn.setAttribute('aria-busy', 'true');
      T.cart.add({ id: Number(btn.dataset.variantId), quantity: 1 }, { open: false })
        .catch((error) => { if (err) err.textContent = error.message || T.strings.error; })
        .finally(() => btn.removeAttribute('aria-busy'));
      return true;
    }
    if (btn.matches('[data-ask]')) {
      const panel = root.querySelector(`#${CSS.escape(btn.dataset.ask)}`);
      const show = Boolean(panel?.hidden);
      // Switching from one link to the other: the empty panel folds in the same frame the new one opens (waiting for
      // the focusout check showed both for a moment, a jump)
      if (show) root.querySelectorAll('[data-ask-panel]').forEach((other) => { if (other !== panel) T.cartUI.foldIfEmpty(other); });
      T.cartUI.ask(root, btn.dataset.ask, show);
      if (show) T.cartUI.enter(panel);
      if (show) panel.querySelector('input, textarea')?.focus({ preventScroll: true });
      if (show) panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      return true;
    }
    const line = btn.closest('[data-line-key]');
    if (btn.matches('[data-qty-step]') && line) { T.cartUI.step(root, line, Number(btn.dataset.qtyStep), wait); return true; }
    if (btn.matches('[data-remove]') && line) {
      if (line.getAttribute('aria-busy') !== 'true') T.cartUI.setQty(root, line, 0, wait); // a second click on a line being removed does nothing
      return true;
    }
    return false;
  };
  /* "+" / "−": quick taps add up. The input shows the new number at once; one request goes out after a short pause,
   * and taps made while a request is running are sent when it finishes (the busy line still takes the taps). */
  const stepState = {};
  T.cartUI.step = (root, line, delta, wait) => {
    const key = line.dataset.lineKey;
    const st = (stepState[key] = stepState[key] || {});
    const input = line.querySelector('input[data-qty]');
    st.qty = Math.max(0, (st.qty ?? Number(input?.value || 0)) + delta);
    if (input) input.value = st.qty;
    // A set's pieces move together (setQty updates the whole group): show it on every piece at once
    if (line.dataset.bundleGroup) root.querySelectorAll(`[data-bundle-group="${CSS.escape(line.dataset.bundleGroup)}"] input[data-qty]`).forEach((i) => { i.value = st.qty; });
    clearTimeout(st.timer);
    const flush = async () => {
      if (st.busy || st.qty === undefined) return;
      const qty = st.qty;
      st.qty = undefined;
      st.busy = true;
      const l = root.querySelector(`[data-line-key="${CSS.escape(key)}"]`);
      if (l) await T.cartUI.setQty(root, l, qty, wait);
      st.busy = false;
      if (st.qty !== undefined) {
        const fresh = root.querySelector(`[data-line-key="${CSS.escape(key)}"] input[data-qty]`);
        if (fresh) { fresh.value = st.qty; flush(); } else st.qty = undefined;
      } else delete stepState[key];
    };
    st.timer = setTimeout(flush, 300);
  };
  T.cartUI.onKeydown = (root, e, wait) => {
    if (e.key === 'Enter' && e.target.matches('[data-discount-input]')) { e.preventDefault(); T.cartUI.applyFromInput(root, wait); }
  };
  /* The gift message is saved quietly while typing. A re-render (gift packaging, a quantity) would put back the note the
   * server had before the save landed, so the typed text is kept and written back after every render. */
  let noteDraft = null;
  const saveNote = T.debounce((value) => T.cart.update({ note: value }, { silent: true }).catch(() => {}), 600);
  /* The business tax ID (cart attribute "Tax ID", printed on the invoice) is saved the same way. */
  let taxDraft = null;
  const saveTax = T.debounce((value) => T.cart.update({ attributes: { 'Tax ID': value.trim().replace(/\s+/g, ' ') } }, { silent: true }).catch(() => {}), 600);
  T.cartUI.restoreNote = (root) => {
    if (noteDraft !== null) root.querySelectorAll('[data-cart-note]').forEach((n) => { if (n.value !== noteDraft) n.value = noteDraft; });
    if (taxDraft !== null) root.querySelectorAll('[data-cart-taxid]').forEach((n) => { if (n.value !== taxDraft) n.value = taxDraft; });
  };
  T.cartUI.onInput = (e) => {
    if (e.target.matches('[data-cart-note]')) { noteDraft = e.target.value; saveNote(noteDraft); }
    if (e.target.matches('[data-cart-taxid]')) { taxDraft = e.target.value; saveTax(taxDraft); }
  };
  T.cartUI.onChange = (root, e, wait) => {
    const t = e.target;
    if (t.matches('input[data-qty]')) { T.cartUI.setQty(root, t.closest('[data-line-key]'), Math.max(0, Math.floor(Number(t.value) || 0)), wait); return true; }
    if (t.matches('[data-gift-packaging]')) {
      const body = { attributes: { 'Gift Packaging': t.checked ? 'Yes' : '' } };
      if (noteDraft !== null) body.note = noteDraft;
      T.cart.update(body).catch(() => { t.checked = !t.checked; });
      return true;
    }
    if (t.matches('[data-cart-note]')) { noteDraft = t.value; saveNote(t.value); return true; }
    if (t.matches('[data-cart-taxid]')) { taxDraft = t.value; saveTax(t.value); return true; }
    return false;
  };

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => T.cartUI.onChange(this, e));
      this.addEventListener('input', (e) => T.cartUI.onInput(e));
      this.addEventListener('keydown', (e) => T.cartUI.onKeydown(this, e));
      this.dialog?.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });
      this.dialog?.addEventListener('close', () => {
        document.documentElement.classList.remove('is-locked');
        if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
        this.opener = null;
      });
      document.addEventListener('cart:rendered', () => this.paint());
      document.querySelectorAll('[data-open-cart]').forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); this.open(); }));
      this.paint();
    }
    open() {
      document.dispatchEvent(new CustomEvent('cart:open'));
      if (!this.dialog || this.dialog.open) return;
      const active = document.activeElement;
      this.opener = active && active !== document.body ? active : document.querySelector('[data-open-cart]');
      this.dialog.showModal();
      document.documentElement.classList.add('is-locked');
      this.paint();
      // The server render's chips may be stale or missing; the first open syncs them from /cart.js.
      if (!this.codesSynced) { this.codesSynced = true; T.cartUI.syncCodes(); }
    }
    close() { T.closeDialog(this.dialog); }
    paint() {
      // The upsells keep their own title ("Complete the set"): the free-shipping bar at the top already says how much is left
      T.cartUI.paint(this);
    }
    async onClick(e) {
      const btn = e.target.closest('button');
      if (!btn) return;
      if (btn.matches('[data-close]')) return this.close();
      if (T.cartUI.onClick(this, e)) return;
    }
  }
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  /* ---------------------------------------------------------- Product card */
  class ProductCard extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('pointerenter', (e) => this.onHover(e), true);
    }
    onHover(e) {
      const sw = e.target.closest?.('[data-swatch]');
      if (sw && matchMedia('(hover: hover)').matches) this.preview(sw);
    }
    preview(sw) {
      const img = this.querySelector('.card-product__img:not(.card-product__img--hover)');
      if (sw.dataset.image && img) {
        img.src = sw.dataset.image;
        img.srcset = sw.dataset.srcset || '';
      }
      let hover = this.querySelector('.card-product__img--hover');
      if (sw.dataset.image2) {
        if (!hover && img) { hover = img.cloneNode(); hover.classList.add('card-product__img--hover'); hover.alt = ''; img.after(hover); }
        if (hover) { hover.src = sw.dataset.image2; hover.removeAttribute('srcset'); hover.hidden = false; }
      } else if (hover) {
        hover.hidden = true;
      }
    }
    async onClick(e) {
      const sw = e.target.closest('[data-swatch]');
      if (sw) {
        e.preventDefault();
        this.querySelectorAll('[data-swatch]').forEach((b) => b.classList.toggle('is-active', b === sw));
        this.preview(sw);
        this.querySelectorAll('[data-card-link]').forEach((a) => (a.href = sw.dataset.url));
        const q = this.querySelector('[data-quick-add]');
        if (q) q.dataset.variantId = sw.dataset.variantId;
        return;
      }
      const q = e.target.closest('[data-quick-add]');
      if (q) {
        e.preventDefault();
        q.setAttribute('aria-busy', 'true');
        try {
          await T.cart.add({ id: Number(q.dataset.variantId), quantity: 1 });
          q.classList.add('is-done');
          const status = this.querySelector('[data-card-status]');
          if (status) { status.textContent = status.dataset.added || T.strings.added || ''; setTimeout(() => (status.textContent = ''), 3000); }
          setTimeout(() => q.classList.remove('is-done'), 1600);
        } catch (err) {
          location.href = this.querySelector('[data-card-link]')?.href || '#';
        } finally {
          q.removeAttribute('aria-busy');
        }
      }
    }
  }
  if (!customElements.get('product-card')) customElements.define('product-card', ProductCard);

  /* ------------------------------------------------------------- Drawers */
  class ModalDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      const id = this.id;
      /* Delegated, so openers re-rendered later (e.g. the collection toolbar after a filter swap) still open it */
      this.onOpener = (e) => {
        const b = e.target.closest?.(`[aria-controls="${CSS.escape(id)}"]`);
        if (!b || this.contains(b)) return;
        e.preventDefault();
        this.open(b);
      };
      document.addEventListener('click', this.onOpener);
      this.dialog.addEventListener('click', (e) => {
        /* iOS can deliver the opening tap's click to the backdrop a moment after the sheet appears:
         * ignore backdrop clicks during the first 450ms so the sheet doesn't close at once */
        if (e.target === this.dialog && performance.now() - (this.openedAt || 0) < 450) return;
        if (e.target === this.dialog || e.target.closest('[data-close]')) this.close();
      });
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('is-locked');
        this.opener?.setAttribute('aria-expanded', 'false');
        this.opener?.focus({ preventScroll: true });
      });
    }
    disconnectedCallback() { document.removeEventListener('click', this.onOpener); }
    /* Content kept in <template data-defer> (the menu drawer) is built on the first open */
    hydrate() {
      this.dialog.querySelectorAll('template[data-defer]').forEach((t) => t.replaceWith(t.content.cloneNode(true)));
    }
    open(opener) {
      this.hydrate();
      this.opener = opener;
      opener?.setAttribute('aria-expanded', 'true');
      /* a dialog left open/closing (back-forward cache, interrupted exit) would make showModal() throw */
      this.dialog.classList.remove('is-closing');
      /* already showing (a second opener click, e.g. touch + click on iOS): keep it, just re-announce */
      if (!this.dialog.open) { try { this.dialog.showModal(); } catch (err) { this.dialog.show(); } }
      this.openedAt = performance.now();
      document.documentElement.classList.add('is-locked');
      this.dispatchEvent(new CustomEvent('modal:open', { bubbles: true }));
    }
    close() { T.closeDialog(this.dialog); }
  }
  if (!customElements.get('modal-dialog')) customElements.define('modal-dialog', ModalDialog);

  /* ------------------------------------------------------------- Header */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      let last = scrollY;
      const onScroll = () => {
        const y = scrollY;
        this.classList.toggle('is-scrolled', y > 8);
        this.classList.toggle('is-hidden', y > 320 && y > last && !this.matches(':focus-within, :hover'));
        last = y;
      };
      addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      this.querySelectorAll('details[data-mega]').forEach((d) => {
        const summary = d.querySelector('summary');
        let t;
        /* Set when hover opened the panel: the click that usually follows the hover must not toggle it shut. */
        let hoverOpened = false;
        d.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse') return;
          clearTimeout(t);
          this.closeOthers(d);
          d.classList.remove('is-closing'); /* back in before it finished closing: keep it open */
          if (!d.open) { hoverOpened = true; d.open = true; }
        });
        d.addEventListener('pointerleave', (e) => {
          if (e.pointerType !== 'mouse') return;
          t = setTimeout(() => {
            if (d.querySelector('select:focus')) return; /* a native select's list sits outside the panel */
            this.shut(d);
          }, 160);
        });
        /* With a mouse the menu opens on hover, so a click on the title goes to its collection (Wallets, Bags…).
         * Keyboard (Enter/Space) and touch keep toggling the panel, which has its own "Shop all" link. */
        let lastPointer = '';
        summary?.addEventListener('pointerdown', (e) => { lastPointer = e.pointerType; });
        summary?.addEventListener('click', (e) => {
          const href = summary.dataset.href;
          if (href && lastPointer === 'mouse' && e.detail > 0) {
            e.preventDefault();
            lastPointer = '';
            if (e.metaKey || e.ctrlKey || e.shiftKey) window.open(href, '_blank', 'noopener');
            else location.href = href;
            return;
          }
          lastPointer = '';
          if (hoverOpened && d.open) { e.preventDefault(); hoverOpened = false; }
        });
        d.addEventListener('toggle', () => {
          summary.setAttribute('aria-expanded', d.open);
          if (d.open) this.closeOthers(d);
          else hoverOpened = false;
        });
        d.addEventListener('keyup', (e) => { if (e.key === 'Escape') { this.shut(d); summary.focus(); } });
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.querySelectorAll('details[data-mega][open]').forEach((d) => this.shut(d));
      });
      const h = () => document.documentElement.style.setProperty('--header-h', `${this.offsetHeight}px`);
      new ResizeObserver(h).observe(this);
    }
    /* Switching straight to another menu closes the old one at once (no overlap) */
    closeOthers(keep) {
      this.querySelectorAll('details[data-mega][open]').forEach((d) => {
        if (d === keep) return;
        d.classList.remove('is-closing');
        d.open = false;
      });
    }

    /* Leaving the menu: the panel rolls back up into the header, then closes */
    shut(d) {
      const panel = d.querySelector(':scope > .mega');
      if (!d.open || d.classList.contains('is-closing')) return;
      if (!panel || matchMedia('(prefers-reduced-motion: reduce)').matches) { d.open = false; return; }
      d.classList.add('is-closing');
      const done = () => {
        if (!d.classList.contains('is-closing')) return; /* reopened meanwhile */
        d.classList.remove('is-closing');
        d.open = false;
      };
      panel.addEventListener('animationend', done, { once: true });
      setTimeout(done, 450); /* safety net if the animation doesn't run */
    }
  }
  if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);

  /* --------------------------------------------------- Predictive search
   * Shopify's suggestions (/search/suggest → sections/predictive-search) and, at the same time, our own search over
   * every text of the website (harberapp services/site-search.ts through /apps/harber/search): FAQ answers, the
   * sentence a product matched in (its description, its page's story, the devices it fits), pages and journal with
   * their sentence, devices, popular searches and "did you mean". Shopify's list shows first; ours is merged in when
   * it arrives. With statistics consent the search the box ended on, and what was opened, go to the Harber admin
   * (Search) through /apps/harber/search/event; the box's token `t` lives only while the panel is open. */
  T.siteSearch = {
    url: '/apps/harber/search',
    consent() { const c = window.Cookiebot && window.Cookiebot.consent; return c ? Boolean(c.statistics) : !window.Cookiebot; },
    device: () => (matchMedia('(pointer: coarse)').matches ? (Math.min(screen.width, screen.height) >= 700 ? 't' : 'm') : 'd'),
    async get(q, t, signal) {
      try {
        const u = new URL(this.url, location.origin);
        u.searchParams.set('q', q);
        u.searchParams.set('lang', T.locale || 'en');
        if (t) u.searchParams.set('t', t);
        const r = await fetch(u, { signal, headers: { Accept: 'application/json' } });
        if (!r.ok) return null;
        const j = await r.json();
        return j && j.ok ? j : null;
      } catch (e) { return null; }
    },
    send(ev) {
      if (!this.consent()) return;
      try { navigator.sendBeacon(`${this.url}/event`, new Blob([JSON.stringify(ev)], { type: 'text/plain' })); } catch (e) { /* ignore */ }
    },
    /** A path from the backend with the market/language prefix of this page. */
    href: (path) => `${(T.routes.root || '/').replace(/\/$/, '')}${path}`,
    /** [[text, matched]] segments as a span with <mark>s (built as text, never HTML). */
    snippet(segs, cls = 'ps__snip') {
      const s = document.createElement('span');
      s.className = cls;
      (segs || []).forEach(([t, m]) => { if (m) { const k = document.createElement('mark'); k.textContent = t; s.append(k); } else s.append(t); });
      return s;
    },
    hasMark: (segs) => (segs || []).some(([, m]) => m)
  };

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-results]');
      this.seq = 0;
      this.token();
      this.input?.addEventListener('input', T.debounce(() => this.search(), 220));
      this.closest('modal-dialog')?.addEventListener('modal:open', () => { this.token(); setTimeout(() => this.input?.focus(), 50); });
      this.results?.addEventListener('click', (e) => this.onClick(e));
      this.querySelector('form')?.addEventListener('submit', () => { this.flush(); this.click('all', `${T.routes.search}?q=${encodeURIComponent(this.input.value)}`); });
    }
    token() { this.t = Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-6); }
    str(k) { return this.dataset[k] || ''; }
    async search() {
      const q = this.input.value.trim();
      this.abort?.abort(); /* before the early return too, or a slow reply refills the cleared results */
      if (q.length < 2) { this.results.innerHTML = ''; this.results.hidden = true; return; }
      this.abort = new AbortController();
      const signal = this.abort.signal;
      const seq = ++this.seq;
      const ours = T.siteSearch.get(q, this.t, signal);
      let shopify = 0;
      try {
        const u = new URL(T.routes.predictiveSearch, location.origin);
        u.searchParams.set('q', q);
        u.searchParams.set('section_id', 'predictive-search');
        u.searchParams.set('resources[type]', 'product,collection,article,page,query');
        u.searchParams.set('resources[limit]', '6');
        u.searchParams.set('resources[limit_scope]', 'each');
        const r = await fetch(u, { signal });
        if (r.ok) {
          const el = parse(await r.text()).querySelector('[data-predictive-results]');
          if (el) shopify = el.querySelectorAll('[data-ps-products] li, .ps__links li').length;
          this.results.replaceChildren(...(el ? [el] : []));
          this.results.hidden = false;
        }
      } catch (e) { if (e.name === 'AbortError') return; console.error(e); }
      const data = await ours;
      if (seq !== this.seq || signal.aborted) return;
      if (data && data.total + data.suggestions.length + (data.correction ? 1 : 0) > 0) {
        try { this.merge(data); } catch (e) { console.error(e); }
      }
      this.pending = { e: 'q', t: this.t, q, r: data ? data.total : 0, p: data ? data.products.length : 0, a: data ? data.answers.length : 0, sh: shopify,
        c: data && data.correction, l: T.locale, co: T.country, d: T.siteSearch.device() };
      clearTimeout(this.logTimer);
      this.logTimer = setTimeout(() => this.flush(), 1500);
    }
    flush() { clearTimeout(this.logTimer); if (this.pending) { T.siteSearch.send(this.pending); this.pending = null; } }
    click(k, u) { T.siteSearch.send({ e: 'click', t: this.t, k, u: String(u).slice(0, 600) }); }
    onClick(e) {
      const a = e.target.closest('a');
      if (!a || !this.results.contains(a)) return;
      if (a.hasAttribute('data-fill')) { /* a suggestion: search it here */
        e.preventDefault();
        this.input.value = a.getAttribute('data-fill');
        this.flush();
        this.search();
        this.input.focus();
        return;
      }
      const k = a.dataset.k || (a.closest('[data-ps-products]') ? 'shopify' : a.matches('.ps__all') ? 'all' : a.closest('.ps__links') ? 'page' : a.matches('[data-ps-query]') ? 'suggestion' : a.closest('.ps__group--chips') ? 'collection' : 'other');
      this.flush();
      this.click(k, a.getAttribute('href') || '');
    }
    el(tag, cls, text) { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; }
    eyebrow(text) { return this.el('p', 'eyebrow', text); }
    /** Our results into Shopify's list (or in its place when it found nothing). */
    merge(d) {
      const S = T.siteSearch;
      let root = this.results.querySelector('[data-predictive-results]');
      if (!root) { root = this.el('div', 'ps'); root.setAttribute('data-predictive-results', ''); this.results.replaceChildren(root); this.results.hidden = false; }
      const empty = root.querySelector('.ps__empty');
      const anchorTop = root.querySelector('[data-order-hint]');
      const insertTop = (node) => { if (anchorTop) anchorTop.after(node); else root.prepend(node); };
      const added = [];
      // Products: the sentence that matched under Shopify's rows, then ours that Shopify didn't list
      let list = root.querySelector('[data-ps-products]');
      const have = new Set();
      list?.querySelectorAll('a.ps__product').forEach((a) => {
        const h = (new URL(a.href, location.origin).pathname.split('/products/')[1] || '').split('/')[0];
        have.add(h);
        const p = d.products.find((x) => x.handle === h);
        if (p && S.hasMark(p.snippet) && !a.querySelector('.ps__why')) a.querySelector('.ps__title')?.after(S.snippet(p.snippet, 'ps__why'));
      });
      const extra = d.products.filter((p) => !have.has(p.handle)).slice(0, Math.max(0, 6 - have.size));
      if (extra.length) {
        if (!list) {
          list = this.el('ul', 'ps__products');
          list.setAttribute('role', 'list');
          list.setAttribute('data-ps-products', '');
          const before = root.querySelector('.ps__links')?.previousElementSibling || root.querySelector('.ps__all');
          const head = this.eyebrow(this.str('strProducts'));
          if (before) { before.before(head); before.before(list); } else root.append(head, list);
        }
        extra.forEach((p) => {
          const li = document.createElement('li');
          const a = this.el('a', 'ps__product');
          a.href = S.href(p.url);
          a.dataset.k = 'product';
          const media = this.el('span', 'ps__media media');
          if (p.image) {
            const img = document.createElement('img');
            img.src = `${p.image}${p.image.includes('?') ? '&' : '?'}width=200`;
            img.alt = ''; img.width = 100; img.height = 100; img.loading = 'lazy';
            media.append(img);
          }
          a.append(media, this.el('span', 'ps__title', p.title));
          if (S.hasMark(p.snippet)) a.append(S.snippet(p.snippet, 'ps__why'));
          li.append(a);
          list.append(li);
        });
        added.push(list);
      }
      // Pages, journal, policies: the sentence that matched, and ours Shopify didn't list
      if (d.pages.length) {
        let links = root.querySelector('.ps__links');
        const paths = new Map();
        links?.querySelectorAll('a').forEach((a) => paths.set(new URL(a.href, location.origin).pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/)/i, ''), a));
        const fresh = [];
        d.pages.forEach((p) => {
          const a = paths.get(p.url.split('?')[0]);
          if (a) { if (S.hasMark(p.snippet) && !a.querySelector('.ps__snip')) { a.classList.add('ps__page'); a.append(S.snippet(p.snippet)); } }
          else fresh.push(p);
        });
        if (fresh.length && (!links || links.children.length < 5)) {
          if (!links) {
            links = this.el('ul', 'ps__links');
            links.setAttribute('role', 'list');
            const all = root.querySelector('.ps__all');
            const head = this.eyebrow(this.str('strPages'));
            if (all) { all.before(head); all.before(links); } else root.append(head, links);
          }
          fresh.slice(0, 5 - links.children.length).forEach((p) => {
            const li = document.createElement('li');
            const a = this.el('a', 'ps__page');
            a.href = S.href(p.url);
            a.dataset.k = p.kind;
            a.append(this.el('span', 'ps__page-title', p.title));
            if (S.hasMark(p.snippet)) a.append(S.snippet(p.snippet));
            li.append(a);
            links.append(li);
          });
          added.push(links);
        }
      }
      // Devices: "iPhone 15 Pro · 12 products fit" → the device finder
      if (d.devices.length) {
        const box = this.el('div', 'ps__group ps__devices');
        box.append(this.eyebrow(this.str('strDevice')));
        d.devices.forEach((v) => {
          const a = this.el('a', 'ps__device');
          a.href = S.href(v.url);
          a.dataset.k = 'device';
          a.append(this.el('span', 'ps__device-name', v.name));
          if (v.products) a.append(this.el('span', 'ps__device-n', this.str('strFit').replace('[count]', v.products)));
          box.append(a);
        });
        insertTop(box);
        added.push(box);
      }
      // FAQ answers, near the top: the question and the sentence of the answer that matched
      if (d.answers.length) {
        const box = this.el('div', 'ps__group ps__answers');
        box.append(this.eyebrow(this.str('strAnswers')));
        d.answers.slice(0, 3).forEach((x) => {
          const a = this.el('a', 'ps__answer');
          a.href = S.href(x.url);
          a.dataset.k = 'answer';
          a.append(this.el('span', 'ps__answer-q', x.title), S.snippet(x.snippet));
          box.append(a);
        });
        insertTop(box);
        added.push(box);
      }
      // Popular searches that start like this, and "did you mean"
      const sugg = [...(d.correction ? [d.correction] : []), ...d.suggestions].filter((v, i, a) => a.indexOf(v) === i).slice(0, 5);
      if (sugg.length) {
        let chips = root.querySelector('.ps__group--chips');
        if (!chips) { chips = this.el('div', 'ps__group ps__group--chips'); insertTop(chips); }
        const first = chips.firstChild;
        sugg.forEach((v, i) => {
          const a = this.el('a', `chip ps__suggest${d.correction && i === 0 ? ' ps__suggest--fix' : ''}`);
          a.href = `${T.routes.search}?q=${encodeURIComponent(v)}`;
          a.setAttribute('data-fill', v);
          a.dataset.k = 'suggestion';
          a.textContent = d.correction && i === 0 ? `${this.str('strDid')} “${v}”?` : v;
          chips.insertBefore(a, first);
        });
        added.push(chips);
      }
      if (empty && added.length) empty.remove();
      if (T.revealItems) T.revealItems(added);
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

  /* "Looking for order #…?" (snippets/search-order-hint.liquid): opens the chat with the question typed instead of
   * the contact page, once the chat widget (harberapp backend/public/chat/widget.js) has loaded. Inside the search
   * modal the chat opens after the modal has closed: closing hands the focus back to the search button. */
  document.addEventListener('click', (e) => {
    const a = e.target.closest?.('[data-order-chat]');
    if (!a || !window.__harberChatReady) return;
    e.preventDefault();
    const open = () => window.dispatchEvent(new CustomEvent('harber:chat-open', { detail: { text: a.getAttribute('data-text') || '' } }));
    const dialog = a.closest('dialog');
    if (!dialog?.open) return open();
    dialog.addEventListener('close', () => setTimeout(open, 0), { once: true });
    T.closeDialog(dialog);
  });

  /* ------------------------------------------------------ Scroll carousel */
  class ScrollCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.dots = this.querySelector('[data-dots]');
      const step = (dir) => this.track.scrollBy({ left: dir * this.track.clientWidth * 0.85, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      this.prev?.addEventListener('click', () => step(-1));
      this.next?.addEventListener('click', () => step(1));
      this.track.addEventListener('scroll', T.debounce(() => this.update(), 60), { passive: true });
      new ResizeObserver(() => this.update()).observe(this.track);
    }
    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      if (this.prev) this.prev.disabled = scrollLeft < 4;
      if (this.next) this.next.disabled = scrollLeft + clientWidth > scrollWidth - 4;
      this.toggleAttribute('data-overflowing', scrollWidth > clientWidth + 4);
      const bar = this.querySelector('.product-recs__progress span');
      if (bar && scrollWidth > clientWidth) {
        const w = (clientWidth / scrollWidth) * 100;
        bar.style.setProperty('--w', `${w}%`);
        bar.style.setProperty('--x', `${(scrollLeft / (scrollWidth - clientWidth)) * (100 / w - 1) * 100}%`);
      }
      if (this.dots) {
        const items = [...this.track.children];
        const i = Math.round(scrollLeft / (items[0]?.offsetWidth || 1));
        this.dots.querySelectorAll('span').forEach((d, n) => d.classList.toggle('is-active', n === Math.min(i, items.length - 1)));
      }
    }
  }
  if (!customElements.get('scroll-carousel')) customElements.define('scroll-carousel', ScrollCarousel);

  /* --------------------------------------------------------- Video embed */
  class DeferredMedia extends HTMLElement {
    connectedCallback() {
      this.querySelector('[data-play]')?.addEventListener('click', () => this.load(), { once: true });
      if (this.hasAttribute('autoplay')) {
        new IntersectionObserver((entries, o) => {
          if (entries.some((e) => e.isIntersecting)) { this.load(); o.disconnect(); }
        }, { rootMargin: '200px' }).observe(this);
      }
    }
    load() {
      const tpl = this.querySelector('template');
      if (!tpl || this.loaded) return;
      this.loaded = true;
      const node = tpl.content.firstElementChild.cloneNode(true);
      this.appendChild(node);
      this.classList.add('is-loaded');
      if (node.tagName === 'VIDEO') node.play?.().catch(() => {});
    }
  }
  if (!customElements.get('deferred-media')) customElements.define('deferred-media', DeferredMedia);

  /* ---------------------------------------------------------- App banner */
  class AppBanner extends HTMLElement {
    connectedCallback() {
      const key = 'harber-app-banner';
      let dismissed = 0;
      try { dismissed = Number(localStorage.getItem(key) || 0); } catch (e) {}
      const isIOSSafari = /iP(hone|ad|od)/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);
      if (Date.now() - dismissed < 14 * 864e5 || isIOSSafari) return;
      setTimeout(() => { this.hidden = false; requestAnimationFrame(() => this.classList.add('is-in')); }, 2500);
      this.querySelector('[data-dismiss]')?.addEventListener('click', () => {
        this.hidden = true;
        try { localStorage.setItem(key, String(Date.now())); } catch (e) {}
      });
    }
  }
  if (!customElements.get('app-banner')) customElements.define('app-banner', AppBanner);

  /* ------------------------------------------------------------- Reveal */
  const io = 'IntersectionObserver' in window && !matchMedia('(prefers-reduced-motion: reduce)').matches
    ? new IntersectionObserver((entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); } }), { rootMargin: '0px 0px -8% 0px' })
    : null;
  const reveal = (root = document) => root.querySelectorAll('[data-reveal]:not(.is-visible)').forEach((el) => (io ? io.observe(el) : el.classList.add('is-visible')));
  T.reveal = reveal;

  /* Smooth open/close for <details> accordions everywhere (product info, FAQs, footer menus, filters,
   * cart): the height eases between the summary and the full content, and the content fades in.
   * Header menus (mega menus, country picker) keep their own behaviour. */
  (() => {
    if (!Element.prototype.animate || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const skip = (d) => d.matches('[data-mega], [data-no-anim]') || d.closest('.site-header, site-header, .menu-drawer');
    const ease = 'cubic-bezier(.2, .7, .2, 1)';
    document.addEventListener('click', (e) => {
      const summary = e.target.closest('summary');
      const d = summary?.parentElement;
      if (!d || d.tagName !== 'DETAILS' || summary !== d.querySelector(':scope > summary') || skip(d) || e.defaultPrevented) return;
      e.preventDefault();
      const cs = getComputedStyle(d);
      const chrome = ['paddingTop', 'paddingBottom', 'borderTopWidth', 'borderBottomWidth'].reduce((n, k) => n + (parseFloat(cs[k]) || 0), 0);
      const closed = summary.offsetHeight + chrome;
      const start = d.offsetHeight;
      const opening = !d.open || d.classList.contains('is-closing');
      d._anim?.cancel();
      d.style.overflow = 'hidden';
      let end;
      if (opening) {
        d.classList.remove('is-closing');
        d.open = true;
        end = d.offsetHeight;
        [...d.children].filter((c) => c !== summary).forEach((c) => c.animate({ opacity: [0, 1], transform: ['translateY(-6px)', 'none'] }, { duration: 420, easing: ease }));
      } else {
        d.classList.add('is-closing');
        end = closed;
      }
      const anim = d.animate({ height: [`${start}px`, `${end}px`] }, { duration: opening ? 420 : 320, easing: ease });
      d._anim = anim;
      anim.onfinish = () => {
        if (!opening) d.open = false;
        d.classList.remove('is-closing');
        d.style.overflow = '';
        d._anim = null;
      };
      anim.oncancel = () => { d.style.overflow = ''; };
    });
  })();

  /* Expand / collapse with the same feel as the <details> accordions above. Every custom "Read more",
   * "Show all", "Show more" or show/hide panel goes through these, so nothing on the site jumps.
   *  - Theme.expand(el, toggle, { collapse, items }): animates el's height around a DOM change. `toggle`
   *    flips the state (add/remove a clamp, a class, `hidden` on items…). Opening: toggle, then grow from the
   *    old height. Closing (collapse: true): the end height is measured by toggling twice, the box shrinks with
   *    the content still in place, then toggle() runs at the end (so clamped text isn't cut mid-way).
   *    `items` (selector, relative to el) fades/slides in whatever became visible.
   *  - Theme.slide(panel, open, { hidden }): show/hide a `hidden` panel (height + fade). `hidden` is the value
   *    used when closing (e.g. 'until-found').
   *  - Theme.revealItems(nodes): staggered fade-in for appended rows.
   * prefers-reduced-motion: the change is applied at once. */
  (() => {
    const EASE = 'cubic-bezier(.2, .7, .2, 1)';
    const still = () => !Element.prototype.animate || matchMedia('(prefers-reduced-motion: reduce)').matches;
    const shown = (n) => n.getClientRects().length > 0;
    const revealItems = (nodes) => {
      if (still()) return;
      [...nodes].slice(0, 24).forEach((n, i) => n.animate(
        { opacity: [0, 1], transform: ['translateY(10px)', 'none'] },
        { duration: 420, delay: Math.min(i, 12) * 35, easing: EASE, fill: 'backwards' }));
    };
    const expand = (el, toggle, { collapse = false, items = null } = {}) => new Promise((done) => {
      if (!el || still()) { toggle(); return done(); }
      el._xAnim?.finish();
      const hiddenBefore = items ? [...el.querySelectorAll(items)].filter((n) => !shown(n)) : [];
      const start = el.getBoundingClientRect().height;
      toggle();
      const end = el.getBoundingClientRect().height;
      if (collapse) toggle(); /* measured; keep the open content while shrinking */
      el.style.overflow = 'hidden';
      const anim = el.animate({ height: [`${start}px`, `${end}px`] }, { duration: collapse ? 340 : 440, easing: EASE });
      el._xAnim = anim;
      if (!collapse && hiddenBefore.length) revealItems(hiddenBefore.filter(shown));
      const finish = () => {
        if (collapse) toggle();
        el.style.overflow = '';
        el._xAnim = null;
        done();
      };
      anim.onfinish = finish;
      anim.oncancel = () => { el.style.overflow = ''; el._xAnim = null; done(); };
    });
    const slide = (panel, open, { hidden = true } = {}) => new Promise((done) => {
      if (!panel) return done();
      const hide = () => (hidden === true ? (panel.hidden = true) : panel.setAttribute('hidden', hidden));
      if (still()) { if (open) panel.removeAttribute('hidden'); else hide(); return done(); }
      panel._xAnim?.cancel();
      if (open) {
        panel.removeAttribute('hidden');
        const h = panel.getBoundingClientRect().height;
        panel.style.overflow = 'hidden';
        const anim = panel.animate({ height: ['0px', `${h}px`], opacity: [0, 1] }, { duration: 440, easing: EASE });
        panel._xAnim = anim;
        anim.onfinish = anim.oncancel = () => { panel.style.overflow = ''; panel._xAnim = null; done(); };
      } else {
        if (panel.hidden) return done();
        const h = panel.getBoundingClientRect().height;
        panel.style.overflow = 'hidden';
        const anim = panel.animate({ height: [`${h}px`, '0px'], opacity: [1, 0] }, { duration: 320, easing: EASE });
        panel._xAnim = anim;
        anim.onfinish = () => { hide(); panel.style.overflow = ''; panel._xAnim = null; done(); };
        anim.oncancel = () => { panel.style.overflow = ''; panel._xAnim = null; done(); };
      }
    });
    T.expand = expand;
    T.slide = slide;
    T.revealItems = revealItems;
  })();

  /* Collections: consecutive "Images" story sections (2–3 tiles each) are joined into one swipeable row
   * when they add up to 4+ tiles, so a long stack of category tiles doesn't fill the page. The server
   * HTML (read by the iOS app) is unchanged; this only moves the tiles on screen. */
  if (document.body.classList.contains('template-collection') && customElements.get('scroll-carousel')) {
    const wrappers = [...document.querySelectorAll('.story-tiles')].map((t) => t.closest('.shopify-section') || t);
    const groups = [];
    wrappers.forEach((w) => {
      const last = groups[groups.length - 1];
      if (last && last[last.length - 1].nextElementSibling === w) last.push(w); else groups.push([w]);
    });
    groups.forEach((group) => {
      const lists = group.map((w) => w.querySelector('.story-tiles__list')).filter(Boolean);
      const items = lists.flatMap((l) => [...l.children]);
      if (items.length < 4) return;
      const first = lists[0];
      const carousel = document.createElement('scroll-carousel');
      carousel.className = 'story-tiles__slider';
      const arrow = (d) => `<svg class="icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="${d}"/></svg>`;
      carousel.innerHTML = `<div class="page-width story-tiles__head"><div class="carousel-nav"><button type="button" data-prev aria-label="${T.strings.previous || ''}">${arrow('M19 12H5M11 6l-6 6 6 6')}</button><button type="button" data-next aria-label="${T.strings.next || ''}">${arrow('M5 12h14M13 6l6 6-6 6')}</button></div></div>`;
      first.classList.add('scroller');
      first.setAttribute('data-track', '');
      items.forEach((li) => first.append(li));
      const parent = first.parentNode;
      const next = first.nextSibling;
      carousel.append(first);
      const bar = document.createElement('div');
      bar.className = 'page-width';
      bar.innerHTML = '<div class="product-recs__progress" aria-hidden="true"><span></span></div>';
      carousel.append(bar);
      /* insert once complete, so scroll-carousel finds its track when it connects */
      parent.insertBefore(carousel, next);
      group.slice(1).forEach((w) => { w.hidden = true; });
    });
  }

  /* Klaviyo's signup teaser is English-only in Klaviyo: on other languages show the translated label instead
   * (only the teaser text; the popup itself is translated in Klaviyo). */
  if (T.strings.klTeaser && !/^en\b/i.test(document.documentElement.lang || 'en')) {
    const label = `${T.strings.klTeaser} >`;
    const fix = () => document.querySelectorAll("[class*='kl-teaser-'] [role='button'] p").forEach((p) => {
      if (p.dataset.hbTr || !/subscribe/i.test(p.textContent)) return;
      const span = p.querySelector('span') || p;
      span.textContent = label;
      p.dataset.hbTr = '1';
    });
    /* Klaviyo can re-render the teaser, so the page keeps being watched, but cheaply: one check per frame, and only
     * when something was added in or around the teaser */
    let queued = 0;
    new MutationObserver((records) => {
      const teaser = (n) => {
        const el = n.nodeType === 3 ? n.parentElement : n.nodeType === 1 ? n : null;
        return !!el && !!(el.closest("[class*='kl-teaser-']") || el.querySelector("[class*='kl-teaser-']"));
      };
      if (queued || !records.some((r) => [...r.addedNodes].some(teaser))) return;
      queued = requestAnimationFrame(() => { queued = 0; fix(); });
    }).observe(document.body, { childList: true, subtree: true });
    fix();
  }

  /* Klaviyo's sign-up popup (on scroll / exit intent, set in Klaviyo) must not interrupt someone who is buying: on the
   * bag page, once the bag has something in it, or after an option was chosen on a product page, a popup that opens is
   * closed at once (hidden by CSS while `kl-quiet` is on, so it never flashes). Embedded forms and the teaser stay, and a
   * tap on the teaser opens the popup anyway. */
  (() => {
    const root = document.documentElement;
    let chose = false;
    const quiet = () => document.body.dataset.template === 'cart' || chose || !!document.querySelector('.cart-count .cart-count__bubble:not(.wish-count)');
    const mark = () => root.classList.toggle('kl-quiet', quiet());
    /* Only the shopper's own choice counts: product.js also dispatches change events while it sets up the page, and the
     * Harber select menu dispatches its change from script, so a change counts once the shopper has pressed something. */
    let touched = false;
    const touch = (e) => { if (e.isTrusted) touched = true; };
    document.addEventListener('pointerdown', touch, true);
    document.addEventListener('keydown', touch, true);
    document.addEventListener('change', (e) => {
      if (touched && e.target.closest?.('[data-option-select], product-info, .pdp-sticky')) { chose = true; mark(); }
    });
    document.addEventListener('cart:updated', () => setTimeout(mark, 600));
    document.addEventListener('cart:rendered', mark);
    mark();
    /* A popup the shopper asked for (tap on the teaser) always opens */
    let asked = 0;
    document.addEventListener('click', (e) => { if (e.target.closest?.("[class*='kl-teaser-']")) { asked = Date.now(); root.classList.remove('kl-quiet'); } }, true);
    /* One thing at a time on arrival (owner, 25 Oct 2026): a popup that opens while the cookie banner still waits for an
     * answer stays hidden (`kl-wait`, not closed: closing would count as dismissed in Klaviyo) and shows once the visitor
     * has answered. web-push.js reads `data-kl-popup` so its card never shares a page view with the popup. */
    // The banner only shows where Cookiebot asks (geotargeted); elsewhere hasResponse can be false with no banner
    const cookiesPending = () => !!(window.Cookiebot && !window.Cookiebot.hasResponse && document.getElementById('CybotCookiebotDialog')?.getClientRects().length);
    const release = () => setTimeout(() => {
      if (root.classList.contains('kl-wait')) root.classList.add('kl-reveal'); // plays the entrance again (base.css)
      root.classList.remove('kl-wait');
    }, 1200);
    window.addEventListener('CookiebotOnAccept', release);
    window.addEventListener('CookiebotOnDecline', release);
    window.addEventListener('klaviyoForms', (e) => {
      if (e.detail?.type === 'close') { mark(); return; }
      if (e.detail?.type === 'open') {
        root.dataset.klPopup = '1';
        if (cookiesPending()) root.classList.add('kl-wait');
      }
      if (e.detail?.type !== 'open' || !quiet()) return;
      if (Date.now() - asked < 8000) { root.classList.remove('kl-quiet'); return; }
      mark();
      let tries = 0;
      const close = () => {
        const btn = document.querySelector("[role='dialog'][aria-label='POPUP Form'] .klaviyo-close-form, [role='dialog'][aria-label='FLYOUT Form'] .klaviyo-close-form");
        if (btn) btn.click();
        else if (tries++ < 20) setTimeout(close, 100);
        else root.classList.remove('kl-quiet'); // couldn't close it: show it rather than leave an invisible scroll lock
      };
      close();
    });
  })();

  /* Parallax: images inside [data-parallax] drift slower than the page and settle from a slight zoom
   * (1.22 → 1.1) as they cross the viewport. Only on-screen elements are measured, once per frame. */
  (() => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return;
    const live = new Set();
    let queued = false;
    const update = () => {
      queued = false;
      const vh = innerHeight;
      live.forEach((el) => {
        const r = el.getBoundingClientRect();
        const p = Math.min(1, Math.max(0, (vh - r.top) / (vh + r.height)));
        el.style.setProperty('--py', `${((p - 0.5) * -8).toFixed(2)}%`);
        el.style.setProperty('--ps', (1.22 - 0.12 * Math.min(1, p * 1.5)).toFixed(3));
      });
    };
    const request = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? live.add(e.target) : live.delete(e.target)));
      request();
    }, { rootMargin: '15% 0px' });
    T.parallax = (root = document) => root.querySelectorAll('[data-parallax]').forEach((el) => io.observe(el));
    T.parallax();
    addEventListener('scroll', request, { passive: true });
    addEventListener('resize', request);
  })();
  reveal();
  document.addEventListener('shopify:section:load', (e) => { reveal(e.target); T.parallax?.(e.target); });

  /* ---------------------------------------------------------- Promotions */
  T.promos = (() => {
    const el = document.getElementById('HarberPromos');
    if (!el) return [];
    let list = [];
    try { list = JSON.parse(el.textContent || '[]'); } catch (e) { return []; }
    const tz = (el.dataset.tz || '+0000').replace(/^([+-]\d{2})(\d{2})$/, '$1:$2');
    /* "2026-10-07", "2026-10-07 9:30" or "2026-10-07T09:30" in the shop's zone. Empty means no limit (null);
     * anything else that does not parse is NaN, which keeps the promo off rather than running it forever. */
    const at = (v) => {
      const s = String(v || '').trim();
      if (!s) return null;
      const m = /^(\d{4}-\d{2}-\d{2})(?:[ T](\d{1,2}):(\d{2}))?/.exec(s);
      if (!m) return NaN;
      return Date.parse(`${m[1]}T${(m[2] || '0').padStart(2, '0')}:${m[3] || '00'}:00${tz}`);
    };
    const now = Date.now();
    return list.filter((p) => {
      const a = at(p.start), b = at(p.end);
      if (Number.isNaN(a) || Number.isNaN(b)) return false;
      return (a === null || now >= a) && (b === null || now < b);
    });
  })();

  const esc = (t) => String(t || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const appliedCodes = (n) => (n.dataset.applied || '').toLowerCase().split(',').filter(Boolean);
  /* In the cart (`apply` = translated label from the slot), the code button applies the code instead of copying it. */
  const promoHTML = (p, kind, apply = null) => {
    const code = !p.code || (apply && apply.done.includes(p.code.toLowerCase())) ? '' : apply
      ? `<button type="button" class="promo-box__code" data-apply-code="${esc(p.code)}" aria-label="${esc(apply.label + ' ' + p.code)}"><span>${esc(p.code)}</span><small>${esc(apply.label)}</small></button>`
      : p.code ? `<button type="button" class="promo-box__code" data-copy="${esc(p.code)}" aria-label="${esc((T.strings.copyCode || 'Copy code') + ' ' + p.code)}"><span>${esc(p.code)}</span><small data-copy-label>${esc(T.strings.copy || 'Copy')}</small></button>` : '';
    return `<div class="promo-box promo-box--${kind}"><div class="promo-box__copy"><p class="promo-box__headline">${esc(p.headline)}</p>${p.text ? `<p class="promo-box__text">${esc(p.text)}</p>` : ''}</div>${code}</div>`;
  };

  T.renderPromos = (root = document) => {
    const active = T.promos;
    const ids = new Set(active.map((p) => p.id));
    document.querySelectorAll('[data-promo-bar]').forEach((n) => (n.hidden = !ids.has(n.dataset.promoBar)));
    const bar = document.querySelector('.announce');
    if (bar) bar.hidden = ![...bar.querySelectorAll('.announce__track > .announce__item')].some((n) => !n.hidden);
    const fill = (slot, html) => { if (slot.dataset.promoHtml !== html) { slot.dataset.promoHtml = html; slot.innerHTML = html; } };
    root.querySelectorAll('[data-promo-slot="cart"]').forEach((slot) => {
      const apply = slot.dataset.applyLabel ? { label: slot.dataset.applyLabel, done: appliedCodes(slot) } : null;
      fill(slot, active.filter((p) => p.cart).map((p) => promoHTML(p, 'cart', apply)).join(''));
    });
    root.querySelectorAll('[data-promo-slot="product"]').forEach((slot) => {
      const pid = Number(slot.dataset.productId);
      fill(slot, active.filter((p) => p.product && (!p.products || p.products.includes(pid))).map((p) => promoHTML(p, 'product')).join(''));
    });
    root.querySelectorAll('[data-promo-slot="menu"]').forEach((slot) => fill(slot, active.filter((p) => p.bar).slice(0, 1).map((p) => promoHTML(p, 'menu')).join('')));
    const pricePromo = (pid) => active.find((p) => p.percent > 0 && (!p.products || p.products.includes(pid)));
    root.querySelectorAll('[data-promo-price]').forEach((n) => {
      const p = pricePromo(Number(n.dataset.productId));
      const amt = Number(n.dataset.amount);
      if (!p || !amt) { n.hidden = true; return; }
      const after = Math.round((amt * (100 - p.percent)) / 100);
      const txt = (T.strings.promoPrice || '[price] with [code]').replace('[price]', T.formatMoney(after)).replace('[code]', p.code || `-${p.percent}%`);
      const html = `${n.hasAttribute('data-from') ? (T.strings.from || '') + ' ' : ''}${txt}`;
      if (n.textContent !== html.trim()) n.textContent = html.trim();
      n.hidden = false;
    });
    root.querySelectorAll('[data-promo-cart]').forEach((n) => {
      const p = active.find((x) => x.percent > 0 && x.cart && !x.products);
      const amt = Number(n.dataset.amount);
      if (!p || !amt || (p.code && appliedCodes(n).includes(p.code.toLowerCase()))) { n.hidden = true; return; }
      const save = Math.round((amt * p.percent) / 100);
      if (n.dataset.applyLabel && p.code) {
        const txt = (n.dataset.text || T.strings.promoSave || '').replace('[amount]', T.formatMoney(save)).replace('[code]', p.code);
        const html = `<span>${esc(txt)}</span> <button type="button" class="promo-price__apply" data-apply-code="${esc(p.code)}" aria-label="${esc(n.dataset.applyLabel + ' ' + p.code)}">${esc(n.dataset.applyLabel)}</button>`;
        if (n.dataset.promoHtml !== html) { n.dataset.promoHtml = html; n.innerHTML = html; }
      } else {
        const txt = (T.strings.promoSave || 'Save [amount] with code [code] at checkout').replace('[amount]', T.formatMoney(save)).replace('[code]', p.code);
        if (n.textContent !== txt) n.textContent = txt;
      }
      n.hidden = false;
    });
    const cardPromos = active.filter((p) => p.cards && p.badge);
    if (cardPromos.length) {
      root.querySelectorAll('product-card').forEach((card) => {
        if (card.querySelector('[data-promo-badge]')) return;
        const pid = Number(card.dataset.productId);
        const p = cardPromos.find((x) => !x.products || x.products.includes(pid));
        if (!p) return;
        const b = document.createElement('span');
        b.className = 'badge badge--sale';
        b.dataset.promoBadge = '';
        b.textContent = p.badge;
        card.querySelector('.card-product__badges')?.prepend(b);
      });
    }
  };
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy]');
    if (!btn) return;
    try { await navigator.clipboard.writeText(btn.dataset.copy); } catch (err) {}
    const l = btn.querySelector('[data-copy-label]');
    if (l) { const old = l.textContent; l.textContent = T.strings.copied || 'Copied'; setTimeout(() => (l.textContent = old), 1600); }
  });
  T.renderPromos();
  document.addEventListener('cart:rendered', () => T.renderPromos());
  document.addEventListener('shopify:section:load', (e) => T.renderPromos(e.target));
  if (T.promos.length) new MutationObserver(T.debounce(() => T.renderPromos(), 150)).observe(document.body, { childList: true, subtree: true });

  /* -------------------------------------------- Free-shipping amount text */
  /* snippets/free-shipping.liquid prints <span data-fs-amount> when the threshold has to be converted from GBP. */
  T.fillFreeShipping = (root = document) => {
    const cents = T.freeShippingCents();
    if (!cents) return;
    const txt = T.formatMoney(cents).replace(/[.,]00(?!\d)/, '');
    root.querySelectorAll('[data-fs-amount]').forEach((n) => { if (n.textContent !== txt) n.textContent = txt; });
  };
  T.fillFreeShipping();
  document.addEventListener('cart:rendered', () => T.fillFreeShipping());
  document.addEventListener('shopify:section:load', (e) => T.fillFreeShipping(e.target));

  /* Express checkout toggle (phones, base.css): opens the folded wallet buttons under "Checkout". The choice is kept
   * for the page view, so a bag re-render doesn't fold them again. */
  let expressOpen = false;
  const paintExpress = () => document.querySelectorAll('[data-express-toggle]').forEach((b) => {
    b.setAttribute('aria-expanded', String(expressOpen));
    b.nextElementSibling?.classList.toggle('is-open', expressOpen);
  });
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-express-toggle]')) return;
    expressOpen = !expressOpen;
    paintExpress();
  });
  document.addEventListener('cart:rendered', paintExpress);

  /* Spec units (snippets/spec-value, units-toggle): html[data-units] = "m" | "in". Remembered per visitor; the first
   * visit follows the country (inches in the US). Set before the specs block or compare page draws. */
  const UNITS_KEY = 'harber:units';
  const setUnits = (u, save) => {
    document.documentElement.dataset.units = u;
    document.querySelectorAll('[data-units-toggle]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.unitsToggle === u)));
    if (save) { try { localStorage.setItem(UNITS_KEY, u); } catch { /* storage blocked */ } }
  };
  {
    let u = null;
    try { u = localStorage.getItem(UNITS_KEY); } catch { /* storage blocked */ }
    setUnits(u === 'in' || u === 'm' ? u : ((window.Shopify && Shopify.country) || T.country) === 'US' ? 'in' : 'm', false);
  }
  T.units = () => document.documentElement.dataset.units;
  document.addEventListener('click', (e) => {
    const b = e.target.closest?.('[data-units-toggle]');
    if (b) setUnits(b.dataset.unitsToggle, true);
  });
  document.addEventListener('shopify:section:load', () => setUnits(T.units() || 'm', false));

  /* Product page specs block (sections/product-specs.liquid): tabs, size chips, and whether the visitor's device
   * (device finder, localStorage 'harber:device') fits the chosen size. */
  const specDevice = (() => {
    try {
      const d = JSON.parse(localStorage.getItem('harber:device') || 'null');
      const inch = Number((/(\d{1,2}(?:\.\d)?)\s*(?:"|”|″|-inch|inch)/i.exec(d?.name || '') || [])[1]);
      return d?.name && inch >= 7 && inch <= 18 && (d.family === 'macbook' || d.family === 'ipad') ? { name: d.name, inch, family: d.family } : null;
    } catch { return null; }
  })();
  const paintSpecFit = (root) => {
    if (!specDevice) return;
    root.querySelectorAll('[data-fit-laptop]').forEach((dl) => {
      const lap = Number(dl.dataset.fitLaptop) || null;
      const tab = Number(dl.dataset.fitTablet) || null;
      const max = specDevice.family === 'ipad' ? (tab ?? lap) : lap;
      const ok = max != null && specDevice.inch <= max + 0.05;
      const row = dl.querySelector('[data-fit-device]');
      if (!row) return;
      row.querySelector('[data-fit-device-name]').textContent = specDevice.name;
      const res = row.querySelector('[data-fit-device-result]');
      res.textContent = ok ? `✓ ${T.strings.fitsYes || 'Fits'}` : `✕ ${T.strings.fitsNo || 'Too small'}`;
      res.className = ok ? 'is-yes' : 'is-no';
      row.hidden = false;
    });
  };
  document.querySelectorAll('[data-pspecs]').forEach(paintSpecFit);
  document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('[data-pspecs]').forEach(paintSpecFit));
  document.addEventListener('click', (e) => {
    const root = e.target.closest?.('[data-pspecs]');
    if (!root) return;
    const tab = e.target.closest('[data-spec-tab]');
    if (tab) {
      root.querySelectorAll('[data-spec-tab]').forEach((b) => { const on = b === tab; b.setAttribute('aria-selected', String(on)); b.tabIndex = on ? 0 : -1; });
      root.querySelectorAll('[data-spec-panel]').forEach((p) => { p.hidden = p.dataset.specPanel !== tab.dataset.specTab; });
      return;
    }
    const size = e.target.closest('[data-spec-size]');
    if (size && !root.hasAttribute('data-dm-guide')) { /* the size drawing runs its own (dmShow) */
      root.querySelectorAll('[data-spec-size]').forEach((b) => b.setAttribute('aria-pressed', String(b === size)));
      root.querySelectorAll('[data-spec-for]').forEach((dl) => { dl.hidden = dl.dataset.specFor !== size.dataset.specSize; });
    }
  });
  document.addEventListener('keydown', (e) => {
    const tab = e.target.closest?.('[data-spec-tab]');
    if (!tab || (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft')) return;
    const tabs = [...tab.parentElement.querySelectorAll('[data-spec-tab]')];
    const next = tabs[(tabs.indexOf(tab) + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    next.focus(); next.click();
  });

  /* Specs tab measurements (snippets/size-dims-list): the block of the chosen size is marked and follows the picker */
  document.addEventListener('variant:change', (e) => {
    const v = e.detail?.variant;
    document.querySelectorAll('[data-size-dims]').forEach((root) => {
      const pos = Number(root.dataset.sizePos);
      const size = pos && v ? (v[`option${pos}`] ?? v.options?.[pos - 1]) : null;
      if (!size) return;
      root.querySelectorAll('[data-size]').forEach((li) => li.classList.toggle('is-current', li.dataset.size === size));
      root.querySelectorAll('[data-size-group]').forEach((g) => g.classList.toggle('is-current', Boolean(g.querySelector('.is-current'))));
    });
  });

  /* Desk mat size guide (sections/desk-mat-size-guide): one drawing that grows or shrinks to the chosen size — the
   * mat, its dimension lines and labels, the object for scale (data-rw / data-rh) kept centred — on a chip press and when the size picker changes
   * (one chip can stand for several Size values with the same measurements: felt / microfibre padding). */
  const dmDraw = (svg, w, h, rw, rh) => {
    const ml = Number(svg.dataset.ml), base = Number(svg.dataset.base), u = Number(svg.dataset.u) || 1;
    const y = base - h, cx = ml + w / 2, cy = y + h / 2, hx = ml - 45 * u, wy = base + 45 * u;
    /* the parts looked up once per drawing, not on every frame */
    if (!svg._dmEls) svg._dmEls = Object.fromEntries([...svg.querySelectorAll('[data-dm]')].map((el) => [el.dataset.dm, el]));
    const q = (k) => svg._dmEls[k];
    const set = (el, a) => el && Object.entries(a).forEach(([k, v]) => el.setAttribute(k, v));
    set(q('mat'), { y, width: w, height: h });
    set(q('pass'), { x1: cx - 22 * u, x2: cx + 22 * u, y1: y + 14 * u, y2: y + 14 * u });
    set(q('brand'), { x: cx });
    set(q('ref'), { x: cx - rw / 2, y: cy - rh / 2, width: rw, height: rh });
    set(q('w-line'), { x2: ml + w });
    set(q('w-end'), { x1: ml + w, x2: ml + w });
    set(q('w-text'), { x: cx, y: wy + 46 * u });
    set(q('h-line'), { y1: y });
    set(q('h-end'), { y1: y, y2: y });
    set(q('h-text'), { y: cy, transform: `rotate(-90 ${hx} ${cy}) translate(0 ${-18 * u})` });
  };
  const dmShow = (root, chip) => {
    root.querySelectorAll('[data-spec-size]').forEach((b) => b.setAttribute('aria-pressed', String(b === chip)));
    root.querySelectorAll('[data-spec-for]').forEach((x) => { x.hidden = x.dataset.specFor !== chip.dataset.specSize; });
    const svg = root.querySelector('[data-dm-svg]');
    if (!svg) return;
    const mat = svg.querySelector('[data-dm="mat"]');
    const ref = svg.querySelector('[data-dm="ref"]');
    const w0 = Number(mat.getAttribute('width')), h0 = Number(mat.getAttribute('height'));
    const w1 = Number(chip.dataset.w), h1 = Number(chip.dataset.h);
    const rw0 = Number(ref?.getAttribute('width')) || 0, rh0 = Number(ref?.getAttribute('height')) || 0;
    const rw1 = Number(chip.dataset.rw) || rw0, rh1 = Number(chip.dataset.rh) || rh0;
    svg.querySelector('[data-dm="w-text"]').innerHTML = chip.dataset.wLabel;
    svg.querySelector('[data-dm="h-text"]').innerHTML = chip.dataset.hLabel;
    cancelAnimationFrame(svg._dmRaf);
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || (w0 === w1 && h0 === h1 && rw0 === rw1 && rh0 === rh1)) return dmDraw(svg, w1, h1, rw1, rh1);
    const t0 = performance.now(), dur = 650;
    svg.classList.add('is-resizing'); /* no drop shadow while it moves: it would be re-rasterised every frame */
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - p, 3);
      dmDraw(svg, w0 + (w1 - w0) * e, h0 + (h1 - h0) * e, rw0 + (rw1 - rw0) * e, rh0 + (rh1 - rh0) * e);
      if (p < 1) svg._dmRaf = requestAnimationFrame(step);
      else svg.classList.remove('is-resizing');
    };
    svg._dmRaf = requestAnimationFrame(step);
  };
  document.addEventListener('click', (e) => {
    const chip = e.target.closest?.('[data-dm-guide] [data-dm-sizes]');
    if (chip) dmShow(chip.closest('[data-dm-guide]'), chip);
  });
  document.addEventListener('variant:change', (e) => {
    const v = e.detail?.variant;
    document.querySelectorAll('[data-dm-guide]').forEach((root) => {
      const pos = Number(root.dataset.sizePos);
      const size = pos && v ? (v[`option${pos}`] ?? v.options?.[pos - 1]) : null;
      if (!size) return;
      const chip = [...root.querySelectorAll('[data-dm-sizes]')].find((b) => b.dataset.dmSizes.split('||').includes(size));
      if (chip && chip.getAttribute('aria-pressed') !== 'true') dmShow(root, chip);
    });
  });

  /* Apple Watch size guide (sections/watch-size-guide): a row selects its strap size in the product form; the chosen
   * size's rows are marked and follow the picker. */
  const watchPick = (size) => {
    const radio = [...document.querySelectorAll('.pdp [data-option-radio], [data-option-radio]')].find((r) => r.value === size && !r.closest('.pdp-sticky'));
    if (radio) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); return; }
    const sel = [...document.querySelectorAll('[data-option-select]')].find((x) => [...x.options].some((o) => o.value === size));
    if (sel) { sel.value = size; sel.dispatchEvent(new Event('change', { bubbles: true })); }
  };
  document.addEventListener('click', (e) => {
    const row = e.target.closest?.('[data-watch-size]');
    if (row) watchPick(row.dataset.watchSize);
  });
  document.addEventListener('keydown', (e) => {
    const row = e.target.closest?.('[data-watch-size]');
    if (row && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); watchPick(row.dataset.watchSize); }
  });
  document.addEventListener('variant:change', (e) => {
    const v = e.detail?.variant;
    document.querySelectorAll('[data-watch-guide]').forEach((g) => {
      const pos = Number(g.dataset.sizePos);
      const size = pos && v ? (v[`option${pos}`] ?? v.options?.[pos - 1]) : null;
      if (!size) return;
      g.querySelectorAll('[data-watch-size]').forEach((r) => { const on = r.dataset.watchSize === size; r.classList.toggle('is-current', on); r.setAttribute('aria-pressed', String(on)); });
    });
  });

  /* ---------------------------------------------------------- Footer menus */
  /* Collapsed on mobile, open from 750px. */
  const footerMq = matchMedia('(min-width: 750px)');
  const footerMenus = () => document.querySelectorAll('[data-footer-menu]').forEach((d) => (d.open = footerMq.matches));
  footerMenus();
  footerMq.addEventListener?.('change', footerMenus);
  document.addEventListener('shopify:section:load', footerMenus);

  /* ------------------------------------------------------ Localization */
  /* Country and language panel (snippets/localization-modal): the search box filters the A–Z countries by their name
   * in every language of the store (Intl.DisplayNames: en, es, de, fr, it, nl, pl, pt), the ISO code and a few aliases (UK,
   * USA, Holland…), accents ignored. A country found by its name in another language shows that name beside it
   * ("Spain · España"), so a visitor typing in their own language sees what they typed.
   * Submitting (a country or a language) shows the switch is happening while the new market loads. */
  const locNorm = (t) => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const LOC_ALIAS = { GB: 'uk united kingdom great britain england scotland wales britain', US: 'usa america united states', NL: 'holland', AE: 'uae emirates dubai', KR: 'korea', CZ: 'czech republic czechia', CH: 'swiss' };
  const LOC_LANGS = ['en', 'es', 'de', 'fr', 'it', 'nl', 'pl', 'pt'];
  let locNames = [];
  try { locNames = LOC_LANGS.map((l) => new Intl.DisplayNames([l], { type: 'region' })); } catch { /* old browsers: own names only */ }
  const locWords = (t) => t.split(/[\s,()'’.-]+/).filter(Boolean);
  const locHit = (hay, q) => locWords(hay).some((w) => w.startsWith(q)) || (q.length > 2 && hay.includes(q));
  document.addEventListener('input', (e) => {
    const box = e.target.closest?.('[data-loc-search]');
    if (!box) return;
    const root = box.closest('.loc__block');
    const q = locNorm(box.value.trim());
    const rows = [...root.querySelectorAll('[data-loc-row]')];
    rows.forEach((li) => {
      const btn = li.querySelector('button');
      const iso = btn?.value || '';
      const own = li.querySelector('.loc__name')?.textContent.trim() || '';
      if (!li._names) {
        li._names = [...new Set(locNames.map((n) => { try { return n.of(iso); } catch { return null; } }).filter((n) => n && n !== iso))];
        li._base = locNorm(`${own} ${iso} ${LOC_ALIAS[iso] || ''}`);
      }
      // Its own name (or code / alias) first; else a name in another language, shown beside it
      const other = q && !locHit(li._base, q) ? li._names.find((n) => locNorm(n) !== locNorm(own) && locHit(locNorm(n), q)) : null;
      li.hidden = Boolean(q) && !locHit(li._base, q) && !other;
      let alt = li.querySelector('.loc__alt');
      if (other && !alt) { alt = document.createElement('span'); alt.className = 'loc__alt'; li.querySelector('.loc__name')?.after(alt); }
      if (alt) { alt.textContent = other ? `· ${other}` : ''; alt.hidden = !other; }
    });
    const sug = root.querySelector('[data-loc-suggested]');
    if (sug) sug.hidden = Boolean(q);
    const empty = root.querySelector('[data-loc-empty]');
    if (empty) empty.hidden = !q || rows.some((li) => !li.hidden);
  });
  document.addEventListener('submit', (e) => {
    if (!e.target.matches?.('[data-loc-form]')) return;
    document.documentElement.classList.add('is-switching-market');
    document.querySelectorAll('.country-picker__btn').forEach((b) => b.setAttribute('aria-busy', 'true'));
    e.submitter?.setAttribute('aria-busy', 'true');
  });
  /* The panel's A–Z countries (sections/localization-panel) come from the Section Rendering API on the first open,
   * fetched already when the pointer or focus reaches an opener, and go into the page's own form; the page's current
   * country is marked there. On failure the next open tries again. */
  let locLoad = null;
  const locFetch = () => (locLoad = locLoad || fetch(`${location.pathname}?section_id=localization-panel`, { credentials: 'same-origin' })
    .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
    .then((html) => new DOMParser().parseFromString(html, 'text/html').querySelector('[data-loc-all]') || Promise.reject(new Error('empty')))
    .catch((err) => { locLoad = null; throw err; }));
  ['pointerover', 'focusin', 'touchstart'].forEach((type) => document.addEventListener(type, (e) => {
    if (!locLoad && e.target.closest?.('[aria-controls="LocalizationModal"]')) locFetch().catch(() => {});
  }, { passive: true }));
  document.addEventListener('modal:open', async (e) => {
    if (e.target.id !== 'LocalizationModal') return;
    const box = e.target.querySelector('[data-loc-content]');
    if (box?.hasAttribute('aria-busy')) {
      try {
        const list = (await locFetch()).cloneNode(true);
        const cur = e.target.dataset.current;
        list.querySelectorAll('button[name="country_code"]').forEach((b) => {
          /* the section request may have marked the cookie's country: the page's one counts */
          const on = b.value === cur;
          b.classList.toggle('is-current', on);
          if (on) b.setAttribute('aria-current', 'true'); else { b.removeAttribute('aria-current'); b.querySelector('.icon')?.remove(); }
        });
        box.replaceChildren(list); box.removeAttribute('aria-busy');
      } catch { return; }
    }
    /* The search box focused on desktop */
    const search = e.target.querySelector('[data-loc-search]');
    if (search && matchMedia('(hover: hover) and (pointer: fine)').matches) setTimeout(() => search.focus({ preventScroll: true }), 60);
  });
  /* Coming back with the browser's back button: undo the loading state */
  /* ------------------------------------------------------------------ Wishlist
   * Shared with the iOS app. Signed-in customers: the list lives on the Shopify customer (metafield
   * harber.wishlist), read from Liquid (Theme.wishlist.ids) and changed through the app proxy
   * /apps/harber/wishlist. Guests: browser storage ({id, handle}), merged into the account on sign-in.
   * If the proxy is unreachable the change is kept locally and merged later. */
  (() => {
    const W = T.wishlist || { loggedIn: false, ids: [] };
    const KEY = 'harber:wishlist';
    const readLocal = () => { try { const v = JSON.parse(localStorage.getItem(KEY) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; } };
    const writeLocal = (items) => { try { localStorage.setItem(KEY, JSON.stringify(items.slice(0, 100))); } catch { /* private mode */ } };
    let ids = new Set((W.loggedIn ? (W.ids || []) : readLocal().map((x) => x.id)).map(String));

    const render = () => {
      document.querySelectorAll('[data-wishlist-toggle]').forEach((b) => {
        const on = ids.has(String(b.dataset.wishlistToggle));
        b.setAttribute('aria-pressed', String(on));
        b.classList.toggle('is-active', on);
        const label = on ? b.dataset.labelRemove : b.dataset.labelAdd;
        if (label) b.setAttribute('aria-label', label);
      });
      document.querySelectorAll('[data-wishlist-count]').forEach((c) => {
        c.textContent = ids.size ? String(ids.size) : '';
        c.hidden = !ids.size;
      });
    };
    const changed = () => { render(); document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { ids: [...ids] } })); };

    const proxy = async (body) => {
      const r = await fetch(W.endpoint, {
        method: body ? 'POST' : 'GET',
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'same-origin',
      });
      const type = r.headers.get('content-type') || '';
      if (!r.ok || !type.includes('json')) throw new Error(`wishlist ${r.status}`);
      const json = await r.json();
      if (!Array.isArray(json.ids)) throw new Error('wishlist response');
      return json.ids.map(String);
    };

    const toggle = async (btn) => {
      const id = String(btn.dataset.wishlistToggle);
      const adding = !ids.has(id);
      if (adding) ids = new Set([id, ...ids]); else ids.delete(id);
      changed();
      if (adding) {
        // Klaviyo flow "Wishlist reminder" builds its email from these properties (same names as Viewed Product)
        try { window.klaviyo?.push?.(['track', 'Added to Wishlist', { ProductName: btn.dataset.title, ProductID: id, Handle: btn.dataset.handle, ProductURL: btn.dataset.url || undefined, ImageURL: btn.dataset.image || undefined, Price: btn.dataset.price || undefined }]); } catch { /* optional */ }
      }
      const local = () => {
        const items = readLocal().filter((x) => String(x.id) !== id);
        writeLocal(adding ? [{ id, handle: btn.dataset.handle }, ...items] : items);
      };
      if (!W.loggedIn) return local();
      try {
        ids = new Set(await proxy({ action: adding ? 'add' : 'remove', ids: [id] }));
        changed();
      } catch {
        local(); // kept here and merged into the account on the next visit
      }
    };

    document.addEventListener('click', (e) => {
      const b = e.target.closest('[data-wishlist-toggle]');
      if (!b) return;
      e.preventDefault();
      e.stopPropagation();
      toggle(b);
    });

    // Signed in with a guest list on this browser: merge it into the account once.
    if (W.loggedIn) {
      const pending = readLocal();
      if (pending.length) {
        proxy({ action: 'merge', ids: pending.map((x) => x.id) })
          .then((list) => { ids = new Set(list); writeLocal([]); changed(); })
          .catch(() => {});
      }
    }

    // Live: pick up changes made in the app (or another tab) when the page regains focus and every
    // 12s while it is visible.
    if (W.loggedIn) {
      let last = 0;
      const pull = async () => {
        if (document.hidden || Date.now() - last < 4000) return;
        last = Date.now();
        try {
          const list = await proxy();
          if (list.join() !== [...ids].join()) { ids = new Set(list); changed(); }
        } catch { /* offline or proxy down: keep what we have */ }
      };
      document.addEventListener('visibilitychange', pull);
      addEventListener('focus', pull);
      setInterval(pull, 12000);
    }

    T.wishlist = Object.assign(W, { has: (id) => ids.has(String(id)), ids: () => [...ids], local: readLocal, render });
    render();
    document.addEventListener('collection:rendered', render);
    document.addEventListener('shopify:section:load', render);
    new MutationObserver(T.debounce ? T.debounce(render, 80) : render).observe(document.body, { childList: true, subtree: true });
  })();

  /* ------------------------------------------------------------------ Harber Club referral
   * ?ref=CODE on any page is kept for 30 days and written to the cart as the attribute _referral, which
   * the backend reads from the order. Checked once per cart (the cart cookie) and again after adds.
   * Referral programme (harberapp services/referrals.ts): the backend is asked once a day for the friend's offer
   * (POST /apps/harber/referral/offer); while the programme is off it answers "no offer" and nothing else happens.
   * With an offer: a single-use first-order code, added to the bag (keeping other codes) with the cart attribute
   * _referral_offer "name|percent|CODE" (the bag's note, snippets/referral-note.liquid), and a note on arrival.
   * ?ref-test=1 marks this browser for the programme's test mode (?ref-test=0 clears it). */
  (() => {
    const KEY = 'harber:ref';
    const DONE = 'harber:ref-cart';
    const OFFER = 'harber:ref-offer';
    const now = Date.now();
    let ref = null;
    let fresh = false;
    const store = (k, v) => { try { if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, typeof v === 'string' ? v : JSON.stringify(v)); } catch { /* storage blocked */ } };
    const read = (k) => { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch { return null; } };
    try {
      const params = new URLSearchParams(location.search);
      const test = params.get('ref-test');
      if (test === '1' || test === '0') store('harber:ref-offer', null); // ask the backend again (a cached "none" lasts a day)
      if (test === '1') store('harber:ref-test', '1');
      else if (test === '0') store('harber:ref-test', null);
      const fromUrl = params.get('ref');
      if (fromUrl && /^[A-Za-z0-9_-]{3,40}$/.test(fromUrl)) {
        ref = { code: fromUrl, exp: now + 30 * 864e5 };
        fresh = true;
        localStorage.setItem(KEY, JSON.stringify(ref));
      } else {
        ref = JSON.parse(localStorage.getItem(KEY) || 'null');
        if (ref && !(ref.exp > now)) { localStorage.removeItem(KEY); ref = null; }
      }
    } catch { return; }
    if (!ref?.code) return;
    const cartId = () => (document.cookie.match(/(?:^|;\s*)cart=([^;]+)/) || [])[1] || '';
    const getCart = async () => (await fetch(`${T.routes?.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } })).json();
    const update = (body) => fetch(`${T.routes?.cartUpdate || '/cart/update'}.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
    }).then((r) => r.json());

    // The friend's offer for this referral code (cached: the offer until it ends, "none" for a day).
    let offer = read(OFFER);
    if (offer && (offer.ref !== ref.code.toUpperCase() || !(offer.until > now))) offer = null;
    const ask = async () => {
      if (offer) return offer;
      let vid = null;
      try {
        vid = localStorage.getItem('harber:vid');
        if (!vid) { vid = Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(16).padStart(2, '0')).join(''); localStorage.setItem('harber:vid', vid); }
      } catch { return null; }
      let test = false;
      try { test = localStorage.getItem('harber:ref-test') === '1'; } catch { /* */ }
      try {
        const res = await fetch('/apps/harber/referral/offer', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ code: ref.code, vid, test }),
        });
        if (!res.ok) return null;
        const data = await res.json();
        const o = data?.offer;
        offer = o?.code
          ? { ref: ref.code.toUpperCase(), code: o.code, name: o.firstName || '', pct: o.percent, until: Math.min(Date.parse(o.expiresAt) || 0, ref.exp) }
          : { ref: ref.code.toUpperCase(), none: true, until: now + 864e5 };
        store(OFFER, offer);
        return offer;
      } catch { return null; }
    };

    const note = (o) => {
      const S = T.strings || {};
      const esc = (v) => String(v).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
      const title = (o.name ? S.refOffer || '' : S.refOfferAnon || '').replace('[name]', esc(o.name)).replace('[percent]', esc(o.pct));
      if (!title) return;
      const el = document.createElement('aside');
      el.className = 'ref-offer';
      el.setAttribute('role', 'status');
      el.innerHTML = `<p class="ref-offer__title">${title}</p><p class="ref-offer__hint">${S.refOfferHint || ''}</p>`
        + `<button type="button" class="ref-offer__close" aria-label="${esc(S.close || 'Close')}">&times;</button>`;
      const close = () => { el.classList.add('is-closing'); setTimeout(() => el.remove(), 300); };
      el.querySelector('button').addEventListener('click', close);
      document.body.appendChild(el);
      setTimeout(close, 12000);
    };

    const sync = async () => {
      const o = await ask();
      const code = o && !o.none ? o.code : null;
      const marker = `${ref.code}|${code || ''}|${cartId()}`;
      try { if (cartId() && sessionStorage.getItem(DONE) === marker) return; } catch { /* storage blocked */ }
      try {
        const cart = await getCart();
        const attrs = {};
        if (cart.attributes?._referral !== ref.code) attrs._referral = ref.code;
        let applied = cart.discount_codes || [];
        if (code) {
          const value = `${o.name}|${o.pct}|${code}`;
          if (cart.attributes?._referral_offer !== value) attrs._referral_offer = value;
          const codes = applied.map((d) => d.code).filter(Boolean);
          if (!codes.some((c) => c.toUpperCase() === code)) {
            const data = await update({ discount: [...codes, code].join(','), ...(Object.keys(attrs).length ? { attributes: attrs } : {}) });
            applied = data.discount_codes || [];
            Object.keys(attrs).forEach((k) => delete attrs[k]);
            T.cart?.refresh?.();
          }
        }
        if (Object.keys(attrs).length) await update({ attributes: attrs });
        try { sessionStorage.setItem(DONE, `${ref.code}|${code || ''}|${cartId()}`); } catch { /* storage blocked */ }
      } catch { /* retried on the next page */ }
    };
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
    idle(async () => {
      await sync();
      // The note once, when the friend arrives from the link (the bag carries it afterwards).
      if (fresh && offer && !offer.none && !offer.shown) { offer.shown = true; store(OFFER, offer); note(offer); }
    });
    document.addEventListener('cart:updated', () => setTimeout(sync, 300));
  })();

  /* ------------------------------------------------------------------ Device → Klaviyo profile
   * The device chosen in the finder (localStorage 'harber:device') is set on the visitor's Klaviyo profile as
   * harber_device ('MacBook Pro 14"') and harber_device_family ('macbook' / 'ipad' / 'iphone'…), so campaigns
   * can go to the owners of one device. Only for visitors Klaviyo already knows (subscribers, customers), once
   * per change; Klaviyo's script arrives late (app embed), so it is waited for briefly after load. */
  (() => {
    const SENT = 'harber:device-kl';
    let device = null;
    try { device = JSON.parse(localStorage.getItem('harber:device') || 'null'); } catch { return; }
    if (!device?.name) return;
    const name = device.name.replace(/\s*\([^)]*\)/g, '').replace(/\s{2,}/g, ' ').trim() || device.name;
    const props = { harber_device: name, harber_device_family: device.family || null };
    const marker = JSON.stringify(props);
    try { if (localStorage.getItem(SENT) === marker) return; } catch { return; }
    let tries = 0;
    const send = () => {
      const k = window.klaviyo;
      if (!k || typeof k.isIdentified !== 'function' || typeof k.identify !== 'function') {
        if (++tries < 20) setTimeout(send, 1500);
        return;
      }
      Promise.resolve(k.isIdentified()).then((known) => {
        if (!known) return;
        return Promise.resolve(k.identify(props)).then(() => { try { localStorage.setItem(SENT, marker); } catch { /* storage blocked */ } });
      }).catch(() => { /* retried on the next page */ });
    };
    if (document.readyState === 'complete') setTimeout(send, 2000);
    else window.addEventListener('load', () => setTimeout(send, 2000), { once: true });
  })();

  /* ------------------------------------------------------------------ Newsletter forms → Klaviyo
   * The website's newsletter forms (footer, home, newsletter page, journal, password page) and the account sign-up
   * with "subscribe to e-mails" ticked post to Shopify (customer with
   * marketing consent). Shopify's consent doesn't reach Klaviyo as a subscription, so each submit also subscribes
   * the address to Klaviyo's "Email List" through Klaviyo's public client API: the Welcome flow then sends the
   * personal 10% code. keepalive lets the request finish while the form navigates. */
  (() => {
    const COMPANY = 'Y43BXQ'; // Klaviyo public API key (site ID)
    T.klaviyo = { site: COMPANY, list: 'W25Xs6' };
    /* Klaviyo recognises a browser only after an e-mail click or one of its own forms: a signed-in customer, or anyone who
     * just subscribed through our forms, is told to it here, so its sign-up popup (targeting: not to existing
     * profiles) stops showing to them. Klaviyo's script arrives late (app embed): waited for briefly. */
    T.klaviyoIdentify = (email) => {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || '')) return;
      let tries = 0;
      const go = () => {
        const k = window.klaviyo;
        if (!k || typeof k.isIdentified !== 'function' || typeof k.identify !== 'function') { if (++tries < 20) setTimeout(go, 1500); return; }
        Promise.resolve(k.isIdentified()).then((known) => (known ? null : k.identify({ email }))).catch(() => {});
      };
      go();
    };
    if (T.customerEmail) {
      if (document.readyState === 'complete') setTimeout(() => T.klaviyoIdentify(T.customerEmail), 1500);
      else window.addEventListener('load', () => setTimeout(() => T.klaviyoIdentify(T.customerEmail), 1500), { once: true });
    }
    const LIST = 'W25Xs6'; // "Email List", triggers the Welcome flow
    const FORMS = { 'news-form': 'Website footer', 'home-news__form': 'Website home', 'newsletter-page__form': 'Website newsletter page', 'journal-news__form': 'Website journal', 'pw__form': 'Website password page' };
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      // Account sign-up: only when the "subscribe to e-mails" box is ticked
      const signup = form.querySelector('input[name="customer[accepts_marketing]"]');
      const cls = signup ? (signup.checked ? 'signup' : null) : Object.keys(FORMS).find((c) => form.classList.contains(c));
      if (!cls) return;
      const email = (form.querySelector('input[type="email"]')?.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
      try {
        fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${COMPANY}`, {
          method: 'POST', keepalive: true,
          headers: { revision: '2025-10-15', 'Content-Type': 'application/vnd.api+json' },
          body: JSON.stringify({ data: { type: 'subscription', attributes: {
            custom_source: cls === 'signup' ? 'Website account sign-up' : FORMS[cls],
            profile: { data: { type: 'profile', attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } },
          }, relationships: { list: { data: { type: 'list', id: LIST } } } } }),
        }).catch(() => {});
        T.klaviyoIdentify(email);
      } catch { /* the Shopify form still posts */ }
    }, true);
  })();

  /* Harber Club "earn N points" lines (snippets/club-earn.liquid): GBP = amount / Shopify's rate,
   * points = floor(GBP × tier multiplier); the redeem reminder shows GBP values in the buyer's currency. */
  T.clubEarn = () => {
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    document.querySelectorAll('[data-club-earn]').forEach((el) => {
      const points = Math.floor((Number(el.dataset.cents) || 0) / 100 / rate * (Number(el.dataset.mult) || 1));
      const text = el.querySelector('[data-club-earn-text]');
      if (text) text.textContent = (el.dataset.tpl || '').replace('[points]', points.toLocaleString(T.locale || undefined));
      el.hidden = points <= 0;
    });
    document.querySelectorAll('[data-club-money-tpl]').forEach((el) => {
      const fmt = (gbp) => T.formatMoney(Math.round((Number(gbp) || 0) * rate * 100));
      el.textContent = (el.dataset.clubMoneyTpl || '').replace('[web]', fmt(el.dataset.web)).replace('[app]', fmt(el.dataset.app));
    });
  };
  T.clubEarn();
  document.addEventListener('cart:rendered', () => T.clubEarn());
  /* A bag line still carrying the product page's `Preorder` note after its stock came in (snippets/cart-line marks it
   * data-preorder-stale): empty the note on the line, keeping its other properties, so checkout, the order and its
   * e-mails don't say pre-order. Once per line key and page. */
  const staleDone = new Set();
  T.fixStalePreorders = async () => {
    const keys = [...new Set([...document.querySelectorAll('[data-preorder-stale][data-line-key]')].map((el) => el.dataset.lineKey))].filter((k) => !staleDone.has(k));
    if (!keys.length) return;
    keys.forEach((k) => staleDone.add(k));
    try {
      const cart = await (await fetch(`${T.routes.cart}.js`, { headers: { accept: 'application/json' } })).json();
      let changed = false;
      for (const item of cart.items || []) {
        if (!keys.includes(item.key) || !item.properties?.Preorder) continue;
        /* Shopify merges line properties (a missing key isn't removed): an empty value is hidden at checkout and in the
           e-mails, and the backend ignores it */
        const properties = { ...item.properties, Preorder: '' };
        await T.cart.request(T.routes.cartChange + '.js', { id: item.key, quantity: item.quantity, properties });
        changed = true;
      }
      if (changed) await T.cart.refresh();
    } catch { /* the badge is already hidden; the backend decides by stock */ }
  };
  document.addEventListener('cart:rendered', () => T.fixStalePreorders());
  T.fixStalePreorders();

  document.addEventListener('shopify:section:load', () => T.clubEarn());

  /* ------------------------------------------------------------- Cookie banner vs Klaviyo popup
   * Klaviyo closes its popup on any mousedown/touchstart outside the form (document listener), so a tap on the
   * Cookiebot banner (Accept, a tab…) also closed the 10% popup behind it. Those two events stop at the window
   * when they start inside the banner; Cookiebot itself works on click, which still goes through. */
  ['mousedown', 'touchstart'].forEach((t) => window.addEventListener(t, (e) => {
    if (e.target instanceof Element && e.target.closest('#CybotCookiebotDialog')) e.stopPropagation();
  }, { capture: true, passive: true }));

  /* ------------------------------------------------------------- Cookiebot → Shopify consent
   * Shopify's pixels (app and custom pixels: Reddit, OpenAI…) and apps that read the Customer Privacy API follow
   * Shopify's own consent, not Cookiebot's. Every Cookiebot answer (and the stored one on each page) is passed on,
   * so declining in the banner also stops them. Where Shopify asks for consent (Settings → Customer privacy), they
   * wait for it. [data-cookie-settings] anywhere (cookie policy, footer) reopens the banner. */
  const shareConsent = () => {
    const c = window.Cookiebot?.consent;
    if (!c || !window.Cookiebot.hasResponse || !window.Shopify?.loadFeatures) return;
    /* Outside the banner's regions (Cookiebot geotargeting) Cookiebot answers "yes" by itself (consent ID -1): Shopify keeps
     * its own defaults there (US opt-out and Global Privacy Control), so nothing is passed on. */
    if (window.Cookiebot.consentID === '-1' || c.stamp === '-1') return;
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => {
      const cp = window.Shopify.customerPrivacy;
      if (err || !cp?.setTrackingConsent) return;
      const want = { analytics: !!c.statistics, marketing: !!c.marketing, preferences: !!c.preferences, sale_of_data: !!c.marketing };
      const now = cp.currentVisitorConsent?.() || {};
      const same = Object.keys(want).every((k) => now[k] === (want[k] ? 'yes' : 'no'));
      if (!same) cp.setTrackingConsent(want, () => {});
    });
  };
  ['CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline'].forEach((t) => addEventListener(t, shareConsent));
  /* Footer "Your privacy choices" (US state laws): only where Shopify's geolocation says the visitor may opt out of data sharing */
  const choices = document.querySelector('[data-privacy-choices]');
  if (choices && window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => {
      if (!err && window.Shopify.customerPrivacy?.saleOfDataRegion?.()) choices.hidden = false;
    });
  }
  shareConsent();
  document.addEventListener('click', (e) => {
    if (!e.target.closest?.('[data-cookie-settings]') || !window.Cookiebot) return;
    e.preventDefault();
    window.Cookiebot.renew();
  });

  /* Cookie policy: Cookiebot's own list of every cookie (name, provider, purpose, expiry, the visitor's current
   * consent), kept up to date by its monthly scan, drawn into a [data-cookie-declaration] block of the page body.
   * Called again by page-terms.js after a soft swap, since scripts in swapped HTML don't run. */
  T.cookieDeclaration = (root = document) => {
    const cbid = document.querySelector('script[src*="consent.cookiebot.com/"]')?.src.match(/cookiebot\.com\/([0-9a-f-]{36})\//)?.[1] || '8b1e8f4e-6cb8-4240-aa43-0305c7021330'; /* Cookiebot ID (loaded through GTM) */
    root.querySelectorAll('[data-cookie-declaration]:not([data-done])').forEach((box) => {
      if (!cbid) return;
      box.dataset.done = '1';
      document.getElementById('CookieDeclaration')?.removeAttribute('id');
      const s = document.createElement('script');
      s.id = 'CookieDeclaration';
      s.async = true;
      s.src = `https://consent.cookiebot.com/${cbid}/cd.js`;
      s.dataset.culture = (document.documentElement.lang || 'en').slice(0, 2).toUpperCase();
      box.append(s);
    });
  };
  T.cookieDeclaration();

  /* ------------------------------------------------------------- Small selects on touch
   * iOS zooms into a field whose text is under 16px, so on touch the compact selects (sticky bar, "Pairs well
   * with") keep 16px but their text is transparent and a face on top shows the chosen option at the design size
   * (.select-face, base.css). The <select> stays the control; product.js calls Theme.selectFace.refresh(select)
   * after setting a value or relabelling options in code. */
  (() => {
    const SMALL = '.pdp-sticky__select, .pdp-stl__select';
    const touch = matchMedia('(hover: none)');
    const refresh = (sel) => {
      const face = sel?.parentElement?.classList.contains('select-face') ? sel.nextElementSibling : null;
      if (face) face.textContent = sel.selectedOptions[0]?.textContent?.trim() || '';
    };
    const wrap = (sel) => {
      if (!touch.matches || sel.parentElement?.classList.contains('select-face')) return;
      const box = document.createElement('span');
      box.className = 'select-face';
      const face = document.createElement('span');
      face.className = 'select-face__text';
      face.setAttribute('aria-hidden', 'true');
      sel.before(box);
      box.append(sel, face);
      refresh(sel);
    };
    const scan = (root) => {
      if (root.matches?.(SMALL)) wrap(root);
      root.querySelectorAll?.(SMALL).forEach(wrap);
    };
    T.selectFace = { refresh };
    document.addEventListener('change', (e) => { if (e.target.matches?.(SMALL)) refresh(e.target); }, true);
    if (!touch.matches) return;
    scan(document);
    new MutationObserver((list) => list.forEach((m) => m.addedNodes.forEach((n) => { if (n.nodeType === 1 && !n.classList?.contains('select-face')) scan(n); })))
      .observe(document.body, { childList: true, subtree: true });
  })();

  /* ------------------------------------------------------------- Select menus
   * Native <select>s that read as part of the design (product options, sticky bar, "Pairs well with", sort,
   * contact/compare forms) open a Harber-styled listbox on desktop instead of the OS list. The <select> stays
   * the source of truth (value, change events, forms, product.js labels/sold-out marks are re-read on every
   * open) and acts as the combobox; touch devices keep the native picker. */
  (() => {
    const SEL = '[data-option-select], .pdp-sticky__select, .pdp-stl__select, [data-sort-proxy], .contact-form__select, .cmp__select';
    const fine = () => matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reduce = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
    const NEW_RE = /\s*[-–—]\s*new\s*$/i;
    let cur = null; /* { select, panel, items, active, up } */
    let uid = 0;
    let typed = '';
    let typedAt = 0;

    const parts = (o) => {
      const text = (o.textContent || '').trim();
      const value = (o.value || '').trim();
      let main = text;
      let extra = '';
      /* product.js appends the sold-out suffix to the value ("… — Sold out") */
      if (value && text !== value && text.startsWith(value)) { main = value; extra = text.slice(value.length).trim(); }
      let isNew = false;
      if (NEW_RE.test(main)) { isNew = true; main = main.replace(NEW_RE, ''); }
      return { main, extra, isNew };
    };

    const build = (select) => {
      const panel = document.createElement('div');
      panel.className = 'select-menu';
      panel.setAttribute('role', 'listbox');
      panel.id = `SelectMenu-${++uid}`;
      const label = select.getAttribute('aria-label') || (select.id && document.querySelector(`label[for="${CSS.escape(select.id)}"]`)?.textContent.trim()) || '';
      if (label) panel.setAttribute('aria-label', label);
      const items = [];
      [...select.options].forEach((o, i) => {
        if (o.hidden) return;
        const { main, extra, isNew } = parts(o);
        const li = document.createElement('div');
        li.className = 'select-menu__opt';
        li.id = `${panel.id}-${i}`;
        li.setAttribute('role', 'option');
        li.dataset.index = String(i);
        const placeholder = !o.value;
        const off = o.disabled || placeholder;
        li.setAttribute('aria-selected', String(o.selected && !placeholder));
        if (off) li.setAttribute('aria-disabled', 'true');
        if (placeholder) li.classList.add('is-placeholder');
        if (extra) li.classList.add('is-muted');
        const t = document.createElement('span');
        t.className = 'select-menu__text';
        t.textContent = main;
        li.append(t);
        if (isNew) { const tag = document.createElement('span'); tag.className = 'select-menu__tag'; tag.textContent = T.strings?.newTag || 'New'; li.append(tag); }
        if (extra) { const ex = document.createElement('span'); ex.className = 'select-menu__extra'; ex.textContent = extra; li.append(ex); }
        panel.append(li);
        items.push(li);
      });
      return { panel, items };
    };

    const place = () => {
      if (!cur) return;
      const { select, panel } = cur;
      const r = select.getBoundingClientRect();
      if (!r.width || !select.isConnected || getComputedStyle(select).visibility === 'hidden') return close(false);
      const vw = document.documentElement.clientWidth;
      const vh = innerHeight;
      const width = Math.min(Math.max(r.width, 300), vw - 24);
      panel.style.width = `${width}px`;
      const h = Math.min(panel.scrollHeight, 360);
      const below = vh - r.bottom - 12;
      const above = r.top - 12;
      const up = below < Math.min(h, 240) && above > below;
      cur.up = up;
      panel.classList.toggle('is-up', up);
      panel.style.maxHeight = `${Math.max(160, Math.min(360, up ? above : below))}px`;
      const left = Math.min(Math.max(12, r.left), vw - width - 12);
      panel.style.left = `${left}px`;
      if (up) { panel.style.top = ''; panel.style.bottom = `${vh - r.top + 6}px`; } else { panel.style.bottom = ''; panel.style.top = `${r.bottom + 6}px`; }
    };

    const setActive = (i, scroll = true) => {
      if (!cur) return;
      const it = cur.items[i];
      if (!it) return;
      cur.items.forEach((x) => x.classList.toggle('is-active', x === it));
      cur.active = i;
      cur.select.setAttribute('aria-activedescendant', it.id);
      if (scroll) it.scrollIntoView({ block: 'nearest' });
    };
    const enabled = (i) => cur.items[i] && cur.items[i].getAttribute('aria-disabled') !== 'true';
    const step = (from, dir) => {
      for (let i = from + dir; i >= 0 && i < cur.items.length; i += dir) if (enabled(i)) return i;
      return from;
    };

    const choose = (i) => {
      if (!cur || !enabled(i)) return;
      const { select } = cur;
      const idx = Number(cur.items[i].dataset.index);
      const changed = select.selectedIndex !== idx;
      select.selectedIndex = idx;
      close();
      if (changed) {
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    const close = (focus = true) => {
      if (!cur) return;
      const { select, panel } = cur;
      cur = null;
      select.setAttribute('aria-expanded', 'false');
      select.removeAttribute('aria-activedescendant');
      select.removeAttribute('aria-controls');
      select.classList.remove('is-menu-open');
      removeEventListener('scroll', onScroll, true);
      removeEventListener('resize', onResize);
      if (focus) select.focus({ preventScroll: true });
      if (reduce()) { panel.remove(); return; }
      panel.classList.remove('is-open');
      setTimeout(() => panel.remove(), 200);
    };

    let raf = 0;
    const onScroll = (e) => {
      if (cur && cur.panel.contains(e.target)) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(place);
    };
    const onResize = () => close(false);

    const open = (select) => {
      if (cur) close(false);
      const { panel, items } = build(select);
      if (!items.length) return;
      document.body.append(panel);
      cur = { select, panel, items, active: -1, up: false };
      select.setAttribute('aria-expanded', 'true');
      select.setAttribute('aria-controls', panel.id);
      select.classList.add('is-menu-open');
      place();
      const sel = items.findIndex((it) => it.getAttribute('aria-selected') === 'true');
      setActive(sel >= 0 ? sel : step(-1, 1), false);
      const act = items[cur.active];
      if (act) panel.scrollTop = Math.max(0, act.offsetTop - panel.clientHeight / 2 + act.offsetHeight / 2);
      requestAnimationFrame(() => panel.classList.add('is-open'));
      panel.addEventListener('mousedown', (e) => e.preventDefault()); /* keep focus on the select */
      panel.addEventListener('click', (e) => {
        const it = e.target.closest('.select-menu__opt');
        if (it) choose(items.indexOf(it));
      });
      panel.addEventListener('mousemove', (e) => {
        const it = e.target.closest('.select-menu__opt');
        if (it && cur && items.indexOf(it) !== cur.active && enabled(items.indexOf(it))) setActive(items.indexOf(it), false);
      });
      addEventListener('scroll', onScroll, true);
      addEventListener('resize', onResize);
    };

    const isOurs = (el) => el instanceof HTMLSelectElement && !el.multiple && el.size <= 1 && el.matches(SEL) && !el.closest('dialog');
    /* Open the list from script (product page: "Choose a size" pressed before choosing). False where the OS picker is used. */
    T.selectMenu = {
      open(select) {
        if (!isOurs(select) || !fine() || select.disabled) return false;
        select.focus({ preventScroll: true });
        open(select);
        return true;
      }
    };

    document.addEventListener('mousedown', (e) => {
      const select = e.target;
      if (cur && !cur.panel.contains(e.target) && e.target !== cur.select) close(false);
      if (!isOurs(select) || !fine() || select.disabled || e.button !== 0) return;
      e.preventDefault();
      select.focus({ preventScroll: true });
      if (cur && cur.select === select) close();
      else open(select);
    }, true);

    document.addEventListener('keydown', (e) => {
      const select = e.target;
      if (!isOurs(select) || !fine()) return;
      const k = e.key;
      if (!cur || cur.select !== select) {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' ', 'F4'].includes(k) || (e.altKey && k === 'ArrowDown')) { e.preventDefault(); open(select); }
        return;
      }
      if (k === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
      else if (k === 'Tab') close(false);
      else if (k === 'ArrowDown') { e.preventDefault(); setActive(step(cur.active, 1)); }
      else if (k === 'ArrowUp') { e.preventDefault(); setActive(step(cur.active, -1)); }
      else if (k === 'Home') { e.preventDefault(); setActive(step(-1, 1)); }
      else if (k === 'End') { e.preventDefault(); setActive(step(cur.items.length, -1)); }
      else if (k === 'PageDown') { e.preventDefault(); let i = cur.active; for (let n = 0; n < 6; n += 1) i = step(i, 1); setActive(i); }
      else if (k === 'PageUp') { e.preventDefault(); let i = cur.active; for (let n = 0; n < 6; n += 1) i = step(i, -1); setActive(i); }
      else if (k === 'Enter' || k === ' ') { e.preventDefault(); choose(cur.active); }
      else if (k.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        const now = performance.now();
        typed = now - typedAt < 600 ? typed + k.toLowerCase() : k.toLowerCase();
        typedAt = now;
        const n = cur.items.length;
        for (let j = 1; j <= n; j += 1) {
          const i = (cur.active + (typed.length > 1 ? 0 : 1) + j - 1 + n) % n;
          const txt = cur.items[i].querySelector('.select-menu__text').textContent.trim().toLowerCase();
          if (enabled(i) && txt.startsWith(typed)) { setActive(i); break; }
        }
      }
    }, true);

    document.addEventListener('focusout', (e) => {
      if (cur && e.target === cur.select) setTimeout(() => { if (cur && document.activeElement !== cur.select) close(false); }, 0);
    });
    /* A dialog or the cart opening on top closes the list */
    document.addEventListener('cart:updated', () => close(false));
  })();

  /* Back to this tab after a while: the bag may have changed in another tab (or at checkout) */
  let lastSeen = Date.now();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') { lastSeen = Date.now(); return; }
    if (Date.now() - lastSeen < 30000 || !document.querySelector('[data-section-target="cart-count"]')) return;
    T.cart.refresh().catch(() => {});
  });

  addEventListener('pageshow', (e) => {
    document.documentElement.classList.remove('is-switching-market');
    /* Country and language panel left mid-switch: the pressed row usable again, the search cleared */
    document.querySelectorAll('[data-loc-form] button[aria-busy]').forEach((b) => b.removeAttribute('aria-busy'));
    document.querySelectorAll('[data-loc-search]').forEach((x) => { if (x.value) { x.value = ''; x.dispatchEvent(new Event('input', { bubbles: true })); } });
    /* Back-forward cache can restore a page with a sheet still open: start clean */
    if (e.persisted) {
      document.querySelectorAll('dialog[open]').forEach((d) => { d.classList.remove('is-closing'); d.close(); });
      document.documentElement.classList.remove('is-locked');
      /* The bag may have changed meanwhile (checkout, another tab): refresh the count and the drawer */
      T.cart.refresh().then(() => document.dispatchEvent(new CustomEvent('cart:updated', { detail: { action: 'refresh' } }))).catch(() => {});
    }
    document.querySelectorAll('.country-picker__btn[aria-busy]').forEach((b) => b.removeAttribute('aria-busy'));
  });
  /* A page prerendered on hover may have been rendered before the last add to bag: refresh the bag when it is shown */
  if (document.prerendering) {
    document.addEventListener('prerenderingchange', () => {
      T.cart.refresh().then(() => document.dispatchEvent(new CustomEvent('cart:updated', { detail: { action: 'refresh' } }))).catch(() => {});
    }, { once: true });
  }
})();
